import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  getConstructionDesign,
  calculateSectionConstructionDesign,
  calculateConstructionDesign,
  updateContextWithCalculationResults,
  saveConstructionDesignPreservingData,
  getAllSections,
  deleteProjectSections,
  getCpTeis,
} from "../../../../../api/projectEstimatesApi";
import { EstimateContext } from "../Estimate";
import { useEstimateState } from "../EstimateContext";
import { safeNumber } from "../../../../../utils/timeUtils";

const Tenge = () => <span className="tenge">₸</span>;

const TableCell = ({
  value,
  isSum,
  isPercent,
  isRed,
  isGreen,
  isBold,
  isMulti,
}) => (
  <td
    className={[
      isRed ? "red" : "",
      isGreen ? "green" : "",
      isBold ? "bold" : "",
      isSum ? "sum" : "",
      isPercent ? "percent" : "",
      isMulti ? "multi" : "",
    ].join(" ")}
  >
    {isSum && <Tenge />} {isPercent && "% "}
    {value}
  </td>
);

const WorkTab = forwardRef(
  ({ projectId, isSaving = false, isEditable = true }, ref) => {
    /** Раскрытые разделы по section.id — по ID, чтобы не терять раскрытие при смене порядка после калькуляции */
    const [expandedSectionIds, setExpandedSectionIds] = useState(new Set());
    const [sectionDetails, setSectionDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [constructionData, setConstructionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteRowIndex, setDeleteRowIndex] = useState(null);
    const [estimateNotFound, setEstimateNotFound] = useState(false);
    const [editingFields, setEditingFields] = useState({});
    const [savingFields, setSavingFields] = useState({});
    const [calculatingFields, setCalculatingFields] = useState({});
    const [calculatingSections, setCalculatingSections] = useState(new Set());
    // const [savingAll, setSavingAll] = useState(false);
    const [isAnyCalculating, setIsAnyCalculating] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [globalBonusPercent, setGlobalBonusPercent] = useState(30);
    const [showAddSectionModal, setShowAddSectionModal] = useState(false);
    const [isDirectUpdate, setIsDirectUpdate] = useState(false);
    const [sectionsList, setSectionsList] = useState([]);
    /** Выбранные разделы для добавления (мультиселект) — массив id */
    const [selectedSections, setSelectedSections] = useState([]);
    const [loadingSections, setLoadingSections] = useState(false);
    /** Поиск в модалке добавления раздела */
    const [addSectionSearch, setAddSectionSearch] = useState("");
    const [editingGlobalBonus, setEditingGlobalBonus] = useState(false);
    const [tempGlobalBonus, setTempGlobalBonus] = useState(30);
    const [localPercentChanges, setLocalPercentChanges] = useState({});
    /** Учитывать бонусы при расчёте (чекбокс над таблицей) */
    const [considerBonuses, setConsiderBonuses] = useState(true);
    /** % бонусов АУП и АХО (пока только UI, бэкенд будет позже) */
    const [aupAhoBonusPercent, setAupAhoBonusPercent] = useState(0);
    const [editingAupAhoBonus, setEditingAupAhoBonus] = useState(false);
    const [tempAupAhoBonus, setTempAupAhoBonus] = useState(0);
    /** Идёт лайв-калькуляция при переключении чекбокса бонусов */
    const [isCalculatingIncludeBonuses, setIsCalculatingIncludeBonuses] =
      useState(false);
    /** Индекс строки, которую перетаскивают (для drag-and-drop) */
    const [draggedRowIndex, setDraggedRowIndex] = useState(null);
    // Общая ширина колонки "% доля" (под все строки)
    const [percentColWidth, setPercentColWidth] = useState(84);
    // ref для инпута глобальной доли бонусов
    const globalBonusInputRef = React.useRef(null);
    const aupAhoBonusInputRef = React.useRef(null);
    // Карта для стабильного порядка отображения разделов (по ID секции)
    const sectionOrderRef = useRef({});
    /** Время последней применённой калькуляции — чтобы не перезаписать constructionData из контекста сразу после неё */
    const lastCalculationAppliedRef = useRef(0);
    /** Позиция скролла до калькуляции — восстанавливаем после, чтобы страница не прыгала */
    const scrollPositionBeforeCalcRef = useRef(null);

    /** Порядок: Паркинг (если есть), затем "Блок 1, 2, ..." по возрастанию, затем остальные (для block_expenditures). */
    const sortBlocksFirst = useCallback((blockExpenditures) => {
      if (!Array.isArray(blockExpenditures) || blockExpenditures.length === 0)
        return blockExpenditures;
      const blockRegex = /Блок\s*(\d+)/i;
      const isParking = (name) => name.toLowerCase() === "паркинг";
      const withMeta = blockExpenditures.map((item) => {
        const name = (item?.block?.name || "").trim();
        const parking = isParking(name);
        const m = name.match(blockRegex);
        const num = m ? parseInt(m[1], 10) : null;
        return { item, parking, num };
      });
      const parking = withMeta.filter((x) => x.parking).map((x) => x.item);
      const numbered = withMeta
        .filter((x) => !x.parking && x.num !== null)
        .sort((a, b) => a.num - b.num)
        .map((x) => x.item);
      const rest = withMeta
        .filter((x) => !x.parking && x.num === null)
        .map((x) => x.item);
      return [...parking, ...numbered, ...rest];
    }, []);

    /** Сначала блоки вида "Блок N" по возрастанию N, затем остальные разделы (в прежнем порядке). */
    const sortSectionsBlocksFirst = useCallback((sections) => {
      if (!Array.isArray(sections) || sections.length === 0) return sections;
      const blockRegex = /Блок\s*(\d+)/i;
      const withNum = sections.map((s) => {
        const name = (s?.section?.name || "").trim();
        const m = name.match(blockRegex);
        const num = m ? parseInt(m[1], 10) : null;
        return { section: s, num };
      });
      const numbered = withNum
        .filter((x) => x.num !== null)
        .sort((a, b) => a.num - b.num);
      const rest = withNum.filter((x) => x.num === null);
      return [...numbered, ...rest].map((x) => x.section);
    }, []);

    // Нормализуем порядок разделов, чтобы они не "прыгали" после калькуляций
    // Сначала порядок по order_index / ref; затем показываем "Блок 1, Блок 2, ..." по порядку, потом остальные
    const normalizeSectionsOrder = useCallback(
      (sections) => {
        if (!Array.isArray(sections)) {
          return Array.isArray(sections) ? sections : [];
        }

        const currentOrder = sectionOrderRef.current || {};

        // Находим текущий максимальный индекс порядка
        let maxOrder = -1;
        Object.values(currentOrder).forEach((idx) => {
          if (typeof idx === "number" && idx > maxOrder) {
            maxOrder = idx;
          }
        });

        const updatedOrder = { ...currentOrder };
        let hasChanges = false;

        // Регистрируем новые ID секций в конце порядка
        sections.forEach((section) => {
          const id = section?.section?.id;
          if (id === null || id === undefined) return;
          if (updatedOrder[id] === undefined) {
            maxOrder += 1;
            updatedOrder[id] = maxOrder;
            hasChanges = true;
          }
        });

        if (hasChanges) {
          sectionOrderRef.current = updatedOrder;
        }

        // Сортируем секции по зафиксированному порядку ID
        const sorted = [...sections].sort((a, b) => {
          const idA = a?.section?.id;
          const idB = b?.section?.id;
          const orderA =
            idA !== null && idA !== undefined && updatedOrder[idA] !== undefined
              ? updatedOrder[idA]
              : Number.MAX_SAFE_INTEGER;
          const orderB =
            idB !== null && idB !== undefined && updatedOrder[idB] !== undefined
              ? updatedOrder[idB]
              : Number.MAX_SAFE_INTEGER;

          return orderA - orderB;
        });

        // Показываем сначала блоки "Блок 1", "Блок 2", ... по порядку, затем остальные
        const displaySorted = sortSectionsBlocksFirst(sorted);
        const newOrder = {};
        displaySorted.forEach((s, i) => {
          const id = s?.section?.id;
          if (id != null) newOrder[id] = i;
        });
        sectionOrderRef.current = newOrder;

        return displaySorted;
      },
      [sortSectionsBlocksFirst],
    );
    // Массив ID удаленных разделов для отправки при сохранении
    const [deletedSectionIds, setDeletedSectionIds] = useState([]);
    // Выбор типа раздела: "with_blocks" (с блоками) или "without_blocks" (без блоков)
    const [sectionType, setSectionType] = useState("with_blocks");
    // Итоговые данные с бэкенда
    const [totalConstructionDesign, setTotalConstructionDesign] =
      useState(null);

    // Получаем контексты: старый для шапки и новый для общего состояния сметы
    const estimateContext = useContext(EstimateContext);
    const {
      updateSections,
      setSectionDetails: setCtxSectionDetails,
      setEstimateData: setCtxEstimateData,
      setConstructionDesignForSave,
      buildCalculatePayload,
      estimateData,
      sections: contextSections,
      performSave,
    } = useEstimateState();

    // Разделы считаются константами (только из API, без добавления/удаления), если бэкенд вернул sections_editable: false
    const sectionsEditable = estimateData?.sections_editable !== false;

    // Синхронизируем локальные детали секций с контекстом, чтобы не дергать контекст из рендера
    useEffect(() => {
      // Синхронизация деталей секций в контекст после коммита
      setCtxSectionDetails(sectionDetails);
    }, [sectionDetails, setCtxSectionDetails]);

    // Синхронизируем данные из контекста с локальным состоянием
    useEffect(() => {
      // Не обновляем данные из контекста, если только что сделали прямое обновление
      if (isDirectUpdate) {
        setIsDirectUpdate(false); // Сбрасываем флаг
        return;
      }
      // Не перезаписывать constructionData сразу после применения результата калькуляции:
      // контекст может обновиться с задержкой, и мы затрём актуальные данные с API
      if (Date.now() - lastCalculationAppliedRef.current < 150) {
        return;
      }

      if (
        estimateData?.construction_design &&
        estimateData.construction_design.length > 0
      ) {
        // Проверяем, не теряем ли мы разделы при синхронизации
        // Используем функциональную форму setConstructionData, чтобы получить актуальное состояние
        setConstructionData((currentLocal) => {
          const contextSectionIds = new Set(
            estimateData.construction_design
              .map((s) => s.section?.id)
              .filter(Boolean),
          );
          const localSectionIds = new Set(
            currentLocal.map((s) => s.section?.id).filter(Boolean),
          );
          const missingInContext = Array.from(localSectionIds).filter(
            (id) => !contextSectionIds.has(id),
          );

          if (missingInContext.length > 0) {
            console.warn(
              `[WorkTab] ⚠️ При синхронизации из контекста потеряны разделы: ${missingInContext.join(
                ", ",
              )}`,
            );
            console.warn(
              `[WorkTab] Локально разделов: ${localSectionIds.size}, В контексте: ${contextSectionIds.size}`,
            );
            // Не перезаписываем локальные данные, если в контексте меньше разделов
            // Это может означать, что контекст еще не обновился после добавления раздела
            // Вместо этого обновляем контекст локальными данными
            setCtxEstimateData((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                construction_design: currentLocal,
              };
            });
            return currentLocal; // Сохраняем локальные данные
          }

          // Обновляем локальные данные только если контекст содержит все локальные разделы
          return normalizeSectionsOrder(estimateData.construction_design || []);
        });

        // Обновляем total_construction_design из контекста только если локально его нет
        // Это предотвращает перезапись актуальных данных из результата калькуляции
        setTotalConstructionDesign((currentTotal) => {
          // Если локально уже есть данные, не перезаписываем их из контекста
          // (они могли быть обновлены из результата калькуляции)
          if (currentTotal && estimateData?.total_construction_design) {
            // Сравниваем версии или другие признаки, чтобы понять, что новее
            // Пока просто не перезаписываем, если локально уже есть данные
            return currentTotal;
          }
          // Если локально нет данных, берем из контекста
          return estimateData?.total_construction_design || currentTotal;
        });
      }
    }, [
      estimateData?.construction_design,
      estimateData?.total_construction_design,
      isDirectUpdate,
      setCtxEstimateData,
      normalizeSectionsOrder,
    ]);

    // Восстанавливаем позицию скролла после завершения калькуляции, чтобы страница не прыгала
    const prevIsAnyCalculatingRef = useRef(false);
    useEffect(() => {
      const wasCalculating = prevIsAnyCalculatingRef.current;
      prevIsAnyCalculatingRef.current = isAnyCalculating;
      if (
        wasCalculating &&
        !isAnyCalculating &&
        scrollPositionBeforeCalcRef.current != null
      ) {
        const saved = scrollPositionBeforeCalcRef.current;
        scrollPositionBeforeCalcRef.current = null;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, saved);
          });
        });
      }
    }, [isAnyCalculating]);

    // Синхронизируем чекбокс «Учитывать бонусы» с данными сметы (при загрузке с сервера или переключении вкладки)
    useEffect(() => {
      if (estimateData?.include_bonuses !== undefined) {
        setConsiderBonuses(estimateData.include_bonuses);
      }
    }, [estimateData?.include_bonuses]);

    // Синхронизируем детали секций из контекста
    useEffect(() => {
      if (contextSections && Object.keys(contextSections).length > 0) {
        // Мержим данные из контекста с локальными данными, чтобы не потерять локальные изменения
        setSectionDetails((prev) => {
          const merged = { ...prev };
          Object.keys(contextSections).forEach((sectionId) => {
            // Если в контексте есть данные для раздела, обновляем их
            // Но сохраняем локальные изменения, если они есть
            if (contextSections[sectionId]) {
              const rawBlocks =
                prev[sectionId]?.block_expenditures &&
                Array.isArray(prev[sectionId].block_expenditures) &&
                prev[sectionId].block_expenditures.length > 0 &&
                (!contextSections[sectionId].block_expenditures ||
                  contextSections[sectionId].block_expenditures.length === 0)
                  ? prev[sectionId].block_expenditures
                  : contextSections[sectionId].block_expenditures;
              merged[sectionId] = {
                ...contextSections[sectionId],
                block_expenditures: sortBlocksFirst(
                  Array.isArray(rawBlocks) ? rawBlocks : [],
                ),
              };

              // Синхронизируем total_day_efforts из constructionData для Инсоляции
              const section = constructionData.find(
                (s) => s?.section?.id === parseInt(sectionId),
              );
              if (
                section &&
                section.section?.name?.toLowerCase().trim() === "инсоляция"
              ) {
                if (section?.day_efforts?.total !== undefined) {
                  merged[sectionId] = {
                    ...merged[sectionId],
                    total_day_efforts: section.day_efforts.total,
                  };
                }
              }
            }
          });
          return merged;
        });
      }
    }, [contextSections, constructionData, sortBlocksFirst]);

    // Синхронизируем total_day_efforts из constructionData для Инсоляции при изменении constructionData
    // НО только при первоначальной загрузке (если значение отсутствует в sectionDetails)
    // После сохранения sectionDetails обновляется из результата калькуляции, поэтому не перезаписываем
    useEffect(() => {
      if (constructionData && constructionData.length > 0) {
        setSectionDetails((prev) => {
          const updated = { ...prev };
          let hasChanges = false;

          constructionData.forEach((section) => {
            const sectionId = section.section?.id;
            const sectionName = section.section?.name?.toLowerCase().trim();
            if (sectionId && sectionName === "инсоляция") {
              const dayEffortsTotal = section.day_efforts?.total;
              const currentTotalDayEfforts =
                updated[sectionId]?.total_day_efforts;

              // Синхронизируем только если значение в sectionDetails отсутствует или равно 0
              // Это нужно только при первоначальной загрузке данных
              // После сохранения sectionDetails обновляется из результата калькуляции (строка 1355)
              if (
                dayEffortsTotal !== undefined &&
                dayEffortsTotal !== null &&
                dayEffortsTotal > 0 &&
                (currentTotalDayEfforts === undefined ||
                  currentTotalDayEfforts === null ||
                  currentTotalDayEfforts === 0)
              ) {
                updated[sectionId] = {
                  ...updated[sectionId],
                  total_day_efforts: dayEffortsTotal,
                };
                hasChanges = true;
              }
            }
          });

          return hasChanges ? updated : prev;
        });
      }
    }, [constructionData]);

    // При входе в режим редактирования глобального процента — автофокус и выделение
    useEffect(() => {
      if (editingGlobalBonus && globalBonusInputRef.current) {
        try {
          globalBonusInputRef.current.focus();
          const input = globalBonusInputRef.current;
          requestAnimationFrame(() => input.select());
        } catch {}
      }
    }, [editingGlobalBonus]);

    // При входе в режим редактирования % бонусов АУП и АХО — автофокус и выделение
    useEffect(() => {
      if (editingAupAhoBonus && aupAhoBonusInputRef.current) {
        try {
          aupAhoBonusInputRef.current.focus();
          const input = aupAhoBonusInputRef.current;
          requestAnimationFrame(() => input.select());
        } catch {}
      }
    }, [editingAupAhoBonus]);

    // Вычисляем единую ширину для колонки "% доля" на основе самого длинного значения
    useEffect(() => {
      const values = (constructionData || []).map((section) => {
        const sid = section.section?.id;
        const local = localPercentChanges[sid];
        const thr =
          local !== undefined ? local : section.expenditures_percent?.threshold;
        return typeof thr === "number" ? thr.toFixed(2) : String(thr ?? "0");
      });
      const maxLen = values.reduce((m, t) => Math.max(m, (t || "").length), 1);
      const width = Math.max(30, Math.min(120, maxLen * 10 + 16));
      setPercentColWidth(width);
    }, [constructionData, localPercentChanges]);

    // Инициализируем/синхронизируем глобальный процент бонусов из загруженных деталей секций
    useEffect(() => {
      const ids = Object.keys(sectionDetails || {});
      if (!ids.length) return;
      const values = ids
        .map((id) => sectionDetails[id]?.bonus_percent)
        .filter((v) => typeof v === "number" && !Number.isNaN(v));
      if (!values.length) return;

      // НЕ обновляем globalBonusPercent если мы только что изменили threshold_percent
      // Потому что это может привести к неправильному значению из других секций
      if (isDirectUpdate) {
        return;
      }

      // Дополнительная проверка: если values содержат разные значения bonus_percent,
      // это означает что данные пришли от калькуляции threshold_percent, а не от изменения bonus_percent
      const allSame = values.every((v) => v === values[0]);
      if (!allSame) {
        return;
      }

      const first = Math.max(values[0], 0); // 0 допустим для доли бонусов
      const derived = allSame ? first : first;

      setGlobalBonusPercent(derived);
      setTempGlobalBonus(derived);
    }, [sectionDetails, isDirectUpdate, globalBonusPercent]);

    // Вспомогательная функция для проверки, является ли раздел "Инсоляция"
    const isInsolationSection = useCallback(
      (sectionId) => {
        const section = constructionData.find(
          (s) => s.section?.id === sectionId,
        );
        return section?.section?.name?.toLowerCase().trim() === "инсоляция";
      },
      [constructionData],
    );

    // Вспомогательная функция для проверки, является ли раздел "ГП"
    const isGPSection = useCallback(
      (sectionId) => {
        const section = constructionData.find(
          (s) => s.section?.id === sectionId,
        );
        return section?.section?.name?.toLowerCase().trim() === "гп";
      },
      [constructionData],
    );

    // Вспомогательная функция для проверки, является ли раздел единым (без блоков)
    // Такие разделы считаются по всему проекту, а не по блокам
    const isUnifiedSection = useCallback(
      (sectionId) => {
        return isInsolationSection(sectionId) || isGPSection(sectionId);
      },
      [isInsolationSection, isGPSection],
    );

    // Локальная валидация заполненности секций
    const isSectionComplete = useCallback(
      (sectionId) => {
        const det = sectionDetails[sectionId];
        if (!det) return false;
        const avgOk =
          typeof det.average_salary === "number" && det.average_salary > 0;

        // Для раздела "Инсоляция" проверяем наличие total_day_efforts вместо блоков
        if (isInsolationSection(sectionId)) {
          const hasEfforts =
            typeof det.total_day_efforts === "number" &&
            det.total_day_efforts > 0;
          return avgOk && hasEfforts;
        }

        // Для раздела "ГП" проверяем только среднюю ставку (остальное рассчитывается на бэке)
        if (isGPSection(sectionId)) {
          return avgOk;
        }

        const hasBlocks =
          Array.isArray(det.block_expenditures) &&
          det.block_expenditures.length > 0;
        return avgOk && hasBlocks;
      },
      [sectionDetails, isInsolationSection, isGPSection],
    );

    const incompleteSectionIds = useMemo(() => {
      // Не проверяем заполненность разделов во время загрузки данных
      if (loading) {
        return [];
      }

      const ids = (constructionData || [])
        .map((s) => s?.section?.id)
        .filter(Boolean);

      // Проверяем, что для всех разделов есть детали в sectionDetails
      // Если детали еще не загружены, не считаем разделы незаполненными
      const allSectionsHaveDetails = ids.every((sid) => sectionDetails[sid]);
      if (!allSectionsHaveDetails && ids.length > 0) {
        // Данные еще загружаются из контекста, не проверяем валидацию
        return [];
      }

      const incomplete = ids.filter((sid) => {
        const isComplete = isSectionComplete(sid);
        if (!isComplete) {
          const det = sectionDetails[sid];
          const section = constructionData.find((s) => s?.section?.id === sid);
          console.log(
            `[Validation] Раздел ${sid} (${
              section?.section?.name || "неизвестно"
            }) не заполнен:`,
            {
              hasDetails: !!det,
              average_salary: det?.average_salary,
              isInsolation: isInsolationSection(sid),
              isGP: isGPSection(sid),
              total_day_efforts: det?.total_day_efforts,
              block_expenditures_length: det?.block_expenditures?.length || 0,
            },
          );
        }
        return !isComplete;
      });
      return incomplete;
    }, [
      loading,
      constructionData,
      isSectionComplete,
      sectionDetails,
      isInsolationSection,
      isGPSection,
    ]);

    // Экспортируем функции и валидатор через ref
    const hasValidationErrors = useMemo(() => {
      const hasEmptySections = incompleteSectionIds.length > 0;
      // Проверяем, что % доля (threshold - вводится вручную) и средняя ставка (average_salary) > 0
      // только для заполненных разделов (которые имеют детали секций)
      const hasZeroPercents = (constructionData || []).some((s) => {
        const sectionId = s?.section?.id;
        if (!sectionId) {
          return false;
        }
        // Используем функцию isSectionComplete для проверки заполненности раздела
        // Она учитывает разные типы разделов (обычные, Инсоляция, ГП)
        if (!isSectionComplete(sectionId)) {
          return false;
        }
        // Проверяем threshold (вводится вручную) с учетом локальных изменений
        const thresholdPercent =
          localPercentChanges[sectionId] !== undefined
            ? localPercentChanges[sectionId]
            : (s?.expenditures_percent?.threshold ?? 0);

        // Проверяем average_salary из sectionDetails, так как это поле заполняет пользователь
        // base_price_per_m2 может быть вычисляемым и может быть равно 0 даже при заполненном average_salary
        const det = sectionDetails[sectionId];
        const averageSalary = det?.average_salary ?? 0;

        const hasError = thresholdPercent <= 0 || averageSalary <= 0;
        if (hasError) {
          console.log(
            `[Validation] Раздел ${sectionId} (${
              s?.section?.name || "неизвестно"
            }) имеет ошибку валидации:`,
            {
              thresholdPercent,
              averageSalary,
              thresholdFromLocal: localPercentChanges[sectionId] !== undefined,
              thresholdFromData: s?.expenditures_percent?.threshold,
            },
          );
        }
        return hasError;
      });
      const result = hasEmptySections || hasZeroPercents;
      if (result) {
        console.log("[Validation] Ошибки валидации:", {
          hasEmptySections,
          incompleteSectionIds: incompleteSectionIds.length,
          hasZeroPercents,
        });
      }
      return result;
    }, [
      incompleteSectionIds,
      constructionData,
      sectionDetails,
      localPercentChanges,
      isSectionComplete,
    ]);

    // Функция для удаления разделов перед сохранением
    const deleteSectionsBeforeSave = async () => {
      if (deletedSectionIds.length === 0) {
        return; // Нет разделов для удаления
      }

      try {
        console.log(
          "Удаление разделов:",
          deletedSectionIds,
          "для проекта:",
          projectId,
        );

        // Вызываем API для удаления разделов
        const result = await deleteProjectSections(
          projectId,
          deletedSectionIds,
        );
        console.log("Разделы успешно удалены с сервера:", result);

        // Очищаем массив удаленных разделов после успешного удаления
        setDeletedSectionIds([]);
      } catch (error) {
        console.error("Ошибка при удалении разделов:", error);
        console.log("Тип ошибки:", typeof error);
        console.log("Сообщение ошибки:", error?.message);
        console.log("Статус ошибки:", error?.response?.status);

        // Если разделы не найдены (404), возможно они еще не были сохранены на сервере
        // В этом случае просто очищаем массив и продолжаем сохранение
        const errorMessage = String(error?.message || "");
        const statusCode = error?.message?.match(/status:\s*(\d+)/)?.[1];

        console.log("Проверка ошибки 404:", {
          statusCode,
          includes404: errorMessage.includes("404"),
          includesNotFoundRu:
            errorMessage.includes("Разделы не найдены") ||
            errorMessage.includes("не найдены"),
          includesNotFoundEn: errorMessage.toLowerCase().includes("not found"),
        });

        if (
          statusCode === "404" ||
          errorMessage.includes("404") ||
          errorMessage.includes("Разделы не найдены") ||
          errorMessage.includes("не найдены") ||
          errorMessage.toLowerCase().includes("not found")
        ) {
          console.warn(
            `Разделы не найдены на сервере (404) - пропускаем удаление. Удаленные ID: ${deletedSectionIds.join(
              ", ",
            )}`,
          );
          console.log(
            "Разделы уже удалены из UI и estimateData. При сохранении они не попадут в payload.",
          );
          // Разделы уже удалены из UI, поэтому просто очищаем массив
          // При сохранении они не попадут в payload, так как мы удалили их из estimateData
          setDeletedSectionIds([]);
          return; // Продолжаем сохранение без ошибки
        }

        // Для других ошибок пробрасываем исключение
        console.error("Критическая ошибка при удалении разделов:", error);
        throw error;
      }
    };

    // Функция для синхронизации локальных данных с контекстом перед сохранением
    const syncDataBeforeSave = useCallback(() => {
      const orderedList = Array.isArray(constructionData)
        ? constructionData
        : [];
      // Всегда синхронизируем порядок и состав разделов из таблицы в контекст
      setCtxEstimateData((prev) => {
        if (!prev) return prev;
        return { ...prev, construction_design: orderedList };
      });
      // Передаём порядок в ref контекста, чтобы performSave отправил его гарантированно
      // (setState асинхронен, к моменту performSave контекст может ещё не обновиться)
      if (setConstructionDesignForSave) {
        setConstructionDesignForSave(orderedList);
      }
      // Синхронизируем sectionDetails с контекстом
      setCtxSectionDetails(sectionDetails);
    }, [
      constructionData,
      sectionDetails,
      setCtxEstimateData,
      setCtxSectionDetails,
      setConstructionDesignForSave,
    ]);

    useImperativeHandle(ref, () => ({
      handleSaveForEstimate,
      hasValidationErrors: () => hasValidationErrors,
      deleteSectionsBeforeSave,
      syncDataBeforeSave,
    }));

    useEffect(() => {
      const fetchConstructionData = async () => {
        if (!projectId) return;

        try {
          setLoading(true);
          setError(null);
          setEstimateNotFound(false);
          const response = await getConstructionDesign(projectId);

          // Детали секций из одного GET (section_details из construction_design_detailed), без вызовов GET /sections/{id}
          const sectionDetailsFromApi = response.section_details || {};
          const allSectionDetails = {};
          const sectionsList = response.sections || [];
          for (const section of sectionsList) {
            const sectionId = section.section?.id;
            if (sectionId == null) continue;
            const detail = sectionDetailsFromApi[sectionId];
            allSectionDetails[sectionId] = {
              ...(detail || {}),
              section_id: sectionId,
              bonus_percent: detail?.bonus_percent ?? 0,
              average_salary: detail?.average_salary ?? 0,
              block_expenditures: sortBlocksFirst(
                detail?.block_expenditures ?? [],
              ),
            };
          }

          // Сохраняем порядок из API (order_index), чтобы normalizeSectionsOrder его не менял
          sectionOrderRef.current = (sectionsList || []).reduce((acc, s, i) => {
            const id = s?.section?.id;
            if (id != null) acc[id] = s.order_index ?? i;
            return acc;
          }, {});

          const normalizedSections = normalizeSectionsOrder(sectionsList);
          setConstructionData(normalizedSections);
          updateSections(normalizedSections);
          // Устанавливаем глобальный процент бонусов из ответа API (0 допустим)
          const globalBonusPercent =
            response.bonus_percent_construction_design ?? 30;
          setGlobalBonusPercent(globalBonusPercent);
          setTempGlobalBonus(globalBonusPercent);
          const includeBonuses = response.include_bonuses ?? true;
          setConsiderBonuses(includeBonuses);
          setCtxEstimateData((prev) =>
            prev ? { ...prev, include_bonuses: includeBonuses } : prev,
          );

          // Добавляем bonus_percent ко всем секциям
          const sectionDetailsWithBonus = Object.fromEntries(
            Object.entries(allSectionDetails).map(([sid, det]) => [
              sid,
              { ...det, bonus_percent: globalBonusPercent },
            ]),
          );

          // Синхронизируем total_day_efforts из constructionData для Инсоляции
          const sectionDetailsSynced = { ...sectionDetailsWithBonus };
          normalizedSections.forEach((section) => {
            const sectionId = section.section?.id;
            const sectionName = section.section?.name?.toLowerCase().trim();
            // Проверяем, является ли раздел Инсоляцией (используем прямую проверку, так как isInsolationSection может быть недоступна)
            if (sectionId && sectionName === "инсоляция") {
              // Для Инсоляции берем total_day_efforts из day_efforts.total в constructionData
              const dayEffortsTotal = section.day_efforts?.total;
              if (dayEffortsTotal !== undefined && dayEffortsTotal !== null) {
                sectionDetailsSynced[sectionId] = {
                  ...sectionDetailsSynced[sectionId],
                  total_day_efforts: dayEffortsTotal,
                };
              }
            }
          });

          setSectionDetails(sectionDetailsSynced);
          // Синхронизируем детали секций в глобальный контекст
          setCtxSectionDetails(sectionDetailsSynced);

          // Сохраняем итоговые данные с бэкенда
          if (response.total_construction_design) {
            setTotalConstructionDesign(response.total_construction_design);
          }

          // Передаем данные в контекст Estimate для общей шапки
          if (estimateContext && estimateContext.updateHeaderData) {
            estimateContext.updateHeaderData({
              consent_price_construction: response.consent_price_construction,
              cost_price: response.cost_price,
              difference: response.difference,
            });
          }
        } catch (err) {
          // Ошибка при загрузке данных о строительном проектировании

          // Проверяем, является ли ошибка 404 (смета не найдена)
          if (err.message.includes("404")) {
            setEstimateNotFound(true);
          } else {
            setError(err.message);
          }
        } finally {
          setLoading(false);
        }
      };

      fetchConstructionData();
      // Важно: зависимости ограничены projectId, чтобы избежать бесконечных перезагрузок
      // контекстные функции вызывают обновления контекста, что меняет ссылки и триггерит эффект
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const handleExpandRow = async (idx) => {
      // Блокируем раскрытие строк во время сохранения
      if (isSaving) return;

      const sectionId = constructionData[idx]?.section?.id;

      if (!sectionId) return;

      setExpandedSectionIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(sectionId)) {
          newSet.delete(sectionId);
        } else {
          newSet.add(sectionId);
          fetchSectionDetails(idx, sectionId);
        }
        return newSet;
      });
    };

    const fetchSectionDetails = async (idx, sectionId) => {
      try {
        setLoadingDetails((prev) => ({ ...prev, [idx]: true }));

        const currentLocalData = sectionDetails[sectionId];
        const hasLocalBlocks =
          currentLocalData?.block_expenditures &&
          Array.isArray(currentLocalData.block_expenditures) &&
          currentLocalData.block_expenditures.length > 0;

        // Данные из контекста (уже из одного GET) или повторный запрос сметы
        let data = contextSections?.[sectionId];
        if (!data?.block_expenditures?.length) {
          const response = await getConstructionDesign(projectId);
          const detail = (response.section_details || {})[sectionId];
          data = detail
            ? { ...detail, section_id: sectionId }
            : {
                section_id: sectionId,
                bonus_percent: 0,
                average_salary: 0,
                block_expenditures: [],
              };
        } else {
          data = { ...data, section_id: sectionId };
        }

        const sectionData = { ...data, section_id: sectionId };
        const serverHasBlocks =
          sectionData.block_expenditures &&
          Array.isArray(sectionData.block_expenditures) &&
          sectionData.block_expenditures.length > 0;
        if (hasLocalBlocks && !serverHasBlocks) {
          sectionData.block_expenditures = currentLocalData.block_expenditures;
        }

        setSectionDetails((prev) => ({ ...prev, [sectionId]: sectionData }));
        setConstructionData((prev) => [...prev]);
      } catch (err) {
        const currentLocalData = sectionDetails[sectionId];
        const hasLocal =
          currentLocalData?.block_expenditures &&
          Array.isArray(currentLocalData.block_expenditures) &&
          currentLocalData.block_expenditures.length > 0;
        if (hasLocal) {
          console.warn(
            "Не удалось загрузить данные секции с сервера, используем локальные данные",
          );
        } else {
          setError(err.message);
        }
      } finally {
        setLoadingDetails((prev) => ({ ...prev, [idx]: false }));
      }
    };

    const handleDeleteRow = (idx) => {
      // Блокируем удаление во время сохранения
      if (isSaving) return;

      setDeleteRowIndex(idx);
      setShowDeleteModal(true);
    };

    const handleAddSection = async () => {
      setShowAddSectionModal(true);
      setSelectedSections([]);
      setAddSectionSearch("");
      setSectionType("with_blocks");
      setLoadingSections(true);
      try {
        const data = await getAllSections();

        // Получаем ID уже добавленных секций
        const existingSectionIds = new Set();
        constructionData.forEach((section) => {
          if (section.section && section.section.id) {
            existingSectionIds.add(section.section.id);
          }
        });

        // Фильтруем только те секции, которые еще не добавлены
        const availableSections = data
          .filter((section) => !existingSectionIds.has(section.id))
          .map((section) => ({
            value: section.id,
            label: section.name,
            description: section.description ?? "",
          }));

        setSectionsList(availableSections);
      } catch (e) {
        // Ошибка при загрузке секций
        setSectionsList([]);
      } finally {
        setLoadingSections(false);
      }
    };

    // зарезервировано для будущего применения глобального бонуса ко всем разделам

    const startEditGlobalBonus = () => {
      setEditingGlobalBonus(true);
      setTempGlobalBonus(globalBonusPercent);
    };

    const cancelEditGlobalBonus = () => {
      setEditingGlobalBonus(false);
      setTempGlobalBonus(globalBonusPercent);
    };

    const saveEditGlobalBonus = async (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      try {
        const raw = parseFloat(tempGlobalBonus);
        const newBonus =
          Number.isNaN(raw) || raw < 0 ? globalBonusPercent : raw;
        setEditingGlobalBonus(false);
        setGlobalBonusPercent(newBonus);

        // Создаем обновленные детали секций с новым значением bonus_percent
        const updatedSectionDetails = Object.fromEntries(
          Object.entries(sectionDetails).map(([sid, det]) => [
            sid,
            { ...(det || {}), bonus_percent: newBonus },
          ]),
        );

        // Применяем ко всем секциям в локальном состоянии
        setSectionDetails(updatedSectionDetails);

        // Обновляем контекст EstimateContext, чтобы buildCalculatePayload использовал новые данные
        setCtxSectionDetails(updatedSectionDetails);

        // Обновляем estimateData с новым значением bonus_percent_construction_design
        const updatedEstimateData = {
          ...estimateData,
          bonus_percent_construction_design: newBonus,
        };

        // Вызываем калькуляцию по всему проекту с новым значением
        const calculationResult = await calculateConstructionDesign(
          projectId,
          updatedEstimateData,
          updatedSectionDetails,
        );

        const updatedContext = updateContextWithCalculationResults(
          { estimate: updatedEstimateData, sections: updatedSectionDetails },
          calculationResult,
        );

        // Применяем результаты калькуляции в контекст и UI таблицы
        setCtxEstimateData(updatedContext.estimate);
        setCtxSectionDetails(updatedContext.sections);
        setSectionDetails(updatedContext.sections);
        if (calculationResult?.total_construction_design) {
          setTotalConstructionDesign(calculationResult.total_construction_design);
        }
        if (updatedContext.estimate?.construction_design) {
          setIsDirectUpdate(true);
          lastCalculationAppliedRef.current = Date.now();
          setConstructionData(
            normalizeSectionsOrder(updatedContext.estimate.construction_design),
          );
        }

        // Обновляем данные шапки если они есть в результате
        if (estimateContext && estimateContext.updateHeaderData) {
          estimateContext.updateHeaderData(calculationResult || {});
        }
      } catch (e) {
        setErrorMessage(
          "Ошибка при обновлении доли бонусов: " + (e?.message || e),
        );
        setShowErrorModal(true);
      }
    };

    const startEditAupAhoBonus = () => {
      setEditingAupAhoBonus(true);
      setTempAupAhoBonus(aupAhoBonusPercent);
    };

    const cancelEditAupAhoBonus = () => {
      setEditingAupAhoBonus(false);
      setTempAupAhoBonus(aupAhoBonusPercent);
    };

    const saveEditAupAhoBonus = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const raw = parseFloat(tempAupAhoBonus);
      const newVal =
        Number.isNaN(raw) || raw < 0
          ? aupAhoBonusPercent
          : Math.min(100, Math.max(0, raw));
      setEditingAupAhoBonus(false);
      setAupAhoBonusPercent(newVal);
      setTempAupAhoBonus(newVal);
    };

    // Лайв-калькуляция при переключении чекбокса «Учитывать бонусы при расчёте»
    const handleIncludeBonusesChange = useCallback(
      async (checked) => {
        setConsiderBonuses(checked);
        setCtxEstimateData((prev) =>
          prev ? { ...prev, include_bonuses: checked } : prev,
        );
        setIsCalculatingIncludeBonuses(true);
        try {
          const updatedEstimateData = {
            ...estimateData,
            include_bonuses: checked,
          };
          const calculationResult = await calculateConstructionDesign(
            projectId,
            updatedEstimateData,
            sectionDetails,
          );
          const updatedContext = updateContextWithCalculationResults(
            { estimate: updatedEstimateData, sections: sectionDetails },
            calculationResult,
          );
          setCtxEstimateData(updatedContext.estimate);
          setCtxSectionDetails(updatedContext.sections);
          if (calculationResult?.total_construction_design) {
            setTotalConstructionDesign(
              calculationResult.total_construction_design,
            );
          }
          if (estimateContext?.updateHeaderData) {
            estimateContext.updateHeaderData(calculationResult || {});
          }
          if (updatedContext.estimate?.construction_design) {
            setIsDirectUpdate(true);
            lastCalculationAppliedRef.current = Date.now();
            setConstructionData(
              normalizeSectionsOrder(
                updatedContext.estimate.construction_design,
              ),
            );
          }
        } catch (e) {
          setConsiderBonuses(!checked);
          setCtxEstimateData((prev) =>
            prev ? { ...prev, include_bonuses: !checked } : prev,
          );
          setErrorMessage(
            "Ошибка при пересчёте с учётом бонусов: " + (e?.message || e),
          );
          setShowErrorModal(true);
        } finally {
          setIsCalculatingIncludeBonuses(false);
        }
      },
      [
        estimateData,
        sectionDetails,
        projectId,
        setCtxEstimateData,
        setCtxSectionDetails,
        estimateContext,
        normalizeSectionsOrder,
      ],
    );

    const handleConfirmDelete = () => {
      if (deleteRowIndex !== null) {
        const sectionToDelete = constructionData[deleteRowIndex];
        const sectionId = sectionToDelete?.section?.id;

        if (sectionId) {
          // Оптимистично убираем раздел из UI
          const updatedConstructionData = constructionData.filter(
            (_, idx) => idx !== deleteRowIndex,
          );
          const normalizedAfterDelete = normalizeSectionsOrder(
            updatedConstructionData,
          );
          setConstructionData(normalizedAfterDelete);

          // Обновляем контекст разделов
          updateSections(normalizedAfterDelete);

          // Обновляем estimateData в контексте
          setCtxEstimateData((prev) => {
            if (!prev) return prev;
            const updatedDesign = (prev.construction_design || []).filter(
              (s) => s?.section?.id !== sectionId,
            );
            const normalizedDesign = normalizeSectionsOrder(updatedDesign);
            return { ...prev, construction_design: normalizedDesign };
          });

          // Удаляем детали секции из локального состояния
          setSectionDetails((prev) => {
            const newState = { ...prev };
            delete newState[sectionId];
            return newState;
          });

          // Сохраняем ID удаленного раздела для отправки при сохранении
          setDeletedSectionIds((prev) => [...prev, sectionId]);

          // Удаляем раздел из раскрытых по section id
          const deletedSectionId =
            constructionData[deleteRowIndex]?.section?.id;
          if (deletedSectionId != null) {
            setExpandedSectionIds((prev) => {
              const next = new Set(prev);
              next.delete(deletedSectionId);
              return next;
            });
          }
        }

        setShowDeleteModal(false);
        setDeleteRowIndex(null);
      }
    };

    const handleCancelDelete = () => {
      setShowDeleteModal(false);
      setDeleteRowIndex(null);
    };

    const handleCancelAddSection = () => {
      setShowAddSectionModal(false);
      setSelectedSections([]);
      setAddSectionSearch("");
      setSectionType("with_blocks");
    };

    const handleConfirmAddSection = async () => {
      try {
        if (!selectedSections || selectedSections.length === 0) return;
        if (sectionsList.length === 0) return;

        const existingIds = new Set(
          constructionData.map((s) => s.section?.id).filter(Boolean),
        );
        const toAdd = sectionsList.filter(
          (s) =>
            selectedSections.includes(s.value) && !existingIds.has(s.value),
        );
        if (toAdd.length === 0) {
          setShowAddSectionModal(false);
          setSelectedSections([]);
          return;
        }

        let projectBlocks = [];
        if (sectionType === "with_blocks") {
          try {
            const projectDataResponse = await getCpTeis(projectId);
            projectBlocks = projectDataResponse.blocks || [];
          } catch (err) {
            console.error("Ошибка при загрузке блоков проекта:", err);
          }
        }

        const newRows = [];
        const newDetails = {};

        for (const sectionInfo of toAdd) {
          const newSectionId = parseInt(sectionInfo.value);
          const sectionName = sectionInfo.label?.toLowerCase().trim();
          const isInsolation = sectionName === "инсоляция";
          const isGP = sectionName === "гп";
          const isUnified = isInsolation || isGP;

          let blockExpenditures = [];
          if (!isUnified && sectionType === "with_blocks") {
            blockExpenditures = projectBlocks.map((block) => {
              const teiId = block.tei_id || block.id;
              const blockId = block.block?.id || block.block_id || block.id;
              const blockName =
                block.block?.name || block.name || `Блок ${blockId}`;
              return {
                block: {
                  id: blockId,
                  name: blockName,
                  tei_id: teiId,
                },
                floor_count: 0,
                day_efforts: 0,
                bonus_day_efforts: 0,
                money_expenditures: 0,
                bonus_money_expenditures: 0,
              };
            });
          }

          const newSectionRow = {
            section: {
              id: newSectionId,
              name: sectionInfo.label,
              description: sectionInfo.description ?? "",
            },
            day_efforts: { direct: 0, bonus: 0, total: 0 },
            money_expenditures: {
              direct: 0,
              bonus: 0,
              threshold: 0,
              planned: 0,
            },
            expenditures_percent: { threshold: 0, planned: 0 },
            base_price_per_m2: 0,
            different_in_expenditures: 0,
            payroll_taxes: 0,
            price_with_payroll_taxes: 0,
          };
          newRows.push(newSectionRow);
          newDetails[newSectionId] = {
            section_id: newSectionId,
            section_name: sectionInfo.label,
            average_salary: 0,
            bonus_percent: globalBonusPercent,
            block_expenditures: blockExpenditures,
            ...(isInsolation && { total_day_efforts: 0 }),
          };
        }

        setConstructionData((prev) => {
          const updated = normalizeSectionsOrder([...prev, ...newRows]);
          console.log(
            `[WorkTab] Добавление разделов: +${newRows.length}. Было: ${prev.length}, Стало: ${updated.length}`,
          );
          return updated;
        });

        setSectionDetails((prev) => {
          const updated = { ...prev, ...newDetails };
          setCtxSectionDetails(updated);
          return updated;
        });

        setCtxEstimateData((prevEstimate) => {
          if (!prevEstimate) return prevEstimate;
          const unifiedRows = [];
          const normalRows = [];
          for (const row of newRows) {
            const id = row.section?.id;
            const info = toAdd.find((s) => s.value === id);
            const name = info?.label?.toLowerCase().trim();
            const isUnified = name === "инсоляция" || name === "гп";
            if (isUnified) unifiedRows.push(row);
            else normalRows.push(row);
          }
          const currentProject = Array.isArray(
            prevEstimate.construction_design_project,
          )
            ? prevEstimate.construction_design_project
            : [];
          const current = Array.isArray(prevEstimate.construction_design)
            ? prevEstimate.construction_design
            : [];
          const projectIds = new Set(currentProject.map((s) => s.section?.id));
          const designIds = new Set(current.map((s) => s.section?.id));
          const addToProject = unifiedRows.filter(
            (r) => r.section?.id != null && !projectIds.has(r.section.id),
          );
          const addToDesign = normalRows.filter(
            (r) => r.section?.id != null && !designIds.has(r.section.id),
          );
          return {
            ...prevEstimate,
            construction_design_project:
              addToProject.length > 0
                ? [...currentProject, ...addToProject]
                : currentProject,
            construction_design:
              addToDesign.length > 0
                ? normalizeSectionsOrder([...current, ...addToDesign])
                : current,
          };
        });

        setShowAddSectionModal(false);
        setSelectedSections([]);
        setAddSectionSearch("");
        setSectionType("with_blocks");
      } catch (error) {
        console.error("Ошибка при добавлении секций:", error);
      }
    };

    // Функции для редактирования полей
    const handleEditField = (sectionId, fieldName, value) => {
      // Блокируем редактирование во время сохранения
      if (isSaving) return;

      setEditingFields((prev) => ({
        ...prev,
        [`${sectionId}_${fieldName}`]: value,
      }));
    };

    const handleSaveField = async (sectionId, fieldName, value) => {
      // Блокируем сохранение полей во время общего сохранения
      if (isSaving) return;

      const fieldKey = `${sectionId}_${fieldName}`;

      try {
        setSavingFields((prev) => ({ ...prev, [fieldKey]: true }));

        // Находим детали секции
        const currentSectionDetails = sectionDetails[sectionId];
        if (!currentSectionDetails) {
          console.error("Детали секции не найдены для ID:", sectionId);
          throw new Error(`Детали секции не найдены для ID: ${sectionId}`);
        }

        // Обновляем sectionDetails с новым значением перед подготовкой данных для калькуляции
        // Это нужно, чтобы prepareSectionCalculationData использовала актуальное значение
        const sectionName = constructionData
          .find((s) => s?.section?.id === sectionId)
          ?.section?.name?.toLowerCase()
          .trim();
        const isInsolation = sectionName === "инсоляция";
        const isGP = sectionName === "гп";
        const isUnified = isInsolation || isGP;

        if (fieldName === "total_day_efforts" && isInsolation) {
          // Обновляем sectionDetails синхронно перед вызовом prepareSectionCalculationData
          // Используем функциональное обновление для получения актуального состояния
          setSectionDetails((prev) => ({
            ...prev,
            [sectionId]: {
              ...prev[sectionId],
              total_day_efforts: parseFloat(value),
            },
          }));
        }

        // КРИТИЧНО: Для единых разделов (ГП, Инсоляция) обновляем average_salary перед калькуляцией
        // Это необходимо для того, чтобы buildCalculationPayload использовал актуальное значение
        // и передавал его в initial_data и основной payload
        if (fieldName === "average_salary" && isUnified) {
          const newAverageSalary = parseFloat(value);
          console.log(
            `[WorkTab] Обновляем average_salary для единого раздела ${sectionId} (${sectionName}):`,
            {
              sectionId,
              sectionName,
              oldValue: sectionDetails[sectionId]?.average_salary,
              newValue: newAverageSalary,
              isUnified,
            },
          );

          setSectionDetails((prev) => ({
            ...prev,
            [sectionId]: {
              ...prev[sectionId],
              average_salary: newAverageSalary,
            },
          }));
          // Также обновляем контекст, чтобы buildCalculationPayload использовал актуальное значение
          setCtxSectionDetails((prev) => ({
            ...prev,
            [sectionId]: {
              ...prev[sectionId],
              average_salary: newAverageSalary,
            },
          }));

          console.log(
            `[WorkTab] Обновлены sectionDetails и контекст для секции ${sectionId}`,
          );
        }

        // Подготавливаем данные для отправки
        // Передаем обновленное значение напрямую, чтобы оно использовалось в калькуляции
        const updateData = prepareSectionCalculationData(
          sectionId,
          fieldName,
          value,
        );
        if (!updateData) {
          throw new Error("Не удалось подготовить данные для сохранения");
        }

        // Валидация на нули для критичных полей
        if (fieldName === "average_salary" && parseFloat(value) <= 0) {
          setErrorMessage(
            "Средняя ставка должна быть больше 0. Укажите положительное значение.",
          );
          setShowErrorModal(true);
          return;
        }

        // Если меняем долю бонусов по ПЗ — сразу обновляем детали секции в контексте,
        // чтобы билдер использовал актуальный bonus_percent
        if (fieldName === "bonus_percent") {
          setSectionDetails((prev) => ({
            ...prev,
            [sectionId]: {
              ...(prev[sectionId] || {}),
              bonus_percent: parseFloat(value),
            },
          }));
        }

        // Сохраняем новое значение total_day_efforts для Инсоляции перед калькуляцией
        // чтобы оно не было перезаписано результатом калькуляции
        let savedTotalDayEfforts = null;
        if (fieldName === "total_day_efforts") {
          const sectionName = constructionData
            .find((s) => s?.section?.id === sectionId)
            ?.section?.name?.toLowerCase()
            .trim();
          if (sectionName === "инсоляция") {
            savedTotalDayEfforts = parseFloat(value);
          }
        }

        // Показываем индикатор калькуляции
        setCalculatingFields((prev) => ({ ...prev, [fieldKey]: true }));
        setCalculatingSections((prev) => new Set(prev).add(sectionId));
        setIsAnyCalculating(true);
        // Сохраняем позицию скролла, чтобы после калькуляции страница не прыгала
        scrollPositionBeforeCalcRef.current =
          window.scrollY ?? document.documentElement.scrollTop;

        try {
          // Подготавливаем актуальные данные секций для калькуляции
          // Для единых разделов важно передать актуальное значение average_salary
          const updatedSections = { ...sectionDetails };
          if (fieldName === "average_salary" && isUnified) {
            const newAverageSalary = parseFloat(value);
            updatedSections[sectionId] = {
              ...updatedSections[sectionId],
              average_salary: newAverageSalary,
            };
            console.log(
              `[WorkTab] Подготовлены updatedSections для калькуляции секции ${sectionId}:`,
              {
                sectionId,
                sectionName,
                average_salary: updatedSections[sectionId]?.average_salary,
                total_day_efforts:
                  updatedSections[sectionId]?.total_day_efforts,
              },
            );
          }

          console.log(
            `[WorkTab] Вызываем calculateSectionConstructionDesign для секции ${sectionId}:`,
            {
              sectionId,
              fieldName,
              value,
              updateData,
              updatedSections_sectionId: updatedSections[sectionId],
            },
          );

          // Вызываем калькуляцию для конкретной секции
          const calculationResult = await calculateSectionConstructionDesign(
            {
              estimate: estimateData,
              sections: updatedSections, // Используем обновленные данные секций
            },
            projectId,
            sectionId,
            updateData,
            null, // НЕ передаем payloadData, так как это вызывает обнуление значений
          );

          console.log(
            `[WorkTab] Результат калькуляции для секции ${sectionId}:`,
            {
              sectionId,
              construction_design_project:
                calculationResult?.construction_design_project?.find(
                  (s) => s.section?.id === sectionId,
                ),
            },
          );

          // Логирование для диагностики проблемы с обновлением данных ГП
          if (fieldName === "average_salary") {
            const sectionName = constructionData
              .find((s) => s?.section?.id === sectionId)
              ?.section?.name?.toLowerCase()
              .trim();
            if (sectionName === "гп") {
              console.log("[GP Calculation] Результат калькуляции:", {
                per_section_data: calculationResult.per_section_data?.find(
                  (d) => d.section.id === sectionId,
                ),
                construction_design_project:
                  calculationResult.construction_design_project?.find(
                    (d) => d.section.id === sectionId,
                  ),
                construction_design:
                  calculationResult.construction_design?.find(
                    (d) => d.section.id === sectionId,
                  ),
              });
            }
          }

          // ПРЯМОЕ ОБНОВЛЕНИЕ: Сначала обновляем constructionData из прямого ответа API
          // Это гарантирует, что данные обновятся до обновления контекста
          if (
            calculationResult.construction_design ||
            calculationResult.construction_design_project
          ) {
            setIsDirectUpdate(true);
            lastCalculationAppliedRef.current = Date.now();
            // Объединяем construction_design и construction_design_project из прямого ответа API
            // Дедупликация по section.id: бэкенд может вернуть один и тот же раздел (например ГП) в обоих массивах
            const rawMerged = [
              ...(calculationResult.construction_design || []),
              ...(calculationResult.construction_design_project || []),
            ];
            const seenIds = new Set();
            const allSectionsFromApi = rawMerged.filter((s) => {
              const id = s?.section?.id;
              if (id == null) return true;
              if (seenIds.has(id)) return false;
              seenIds.add(id);
              return true;
            });
            console.log(
              "[GP Calculation] Обновляем constructionData из construction_design/construction_design_project:",
              {
                allSections: allSectionsFromApi,
                gpSection: allSectionsFromApi.find(
                  (s) => s.section?.id === sectionId,
                ),
              },
            );
            // Мержим с текущим constructionData: разделы, которых нет в ответе API (например только что добавленные), не теряем
            setConstructionData((prev) => {
              const fromApi = normalizeSectionsOrder(allSectionsFromApi);
              const apiIds = new Set(
                fromApi.map((s) => s.section?.id).filter(Boolean),
              );
              const missingInApi = (prev || []).filter(
                (s) => s.section?.id != null && !apiIds.has(s.section.id),
              );
              const merged =
                missingInApi.length > 0
                  ? normalizeSectionsOrder([...fromApi, ...missingInApi])
                  : fromApi;
              return merged;
            });

            // Обновляем контекст: один объединённый список в construction_design (без дубликатов),
            // чтобы при сохранении и следующих калькуляциях не терять и не дублировать разделы (в т.ч. ГП)
            // Для единых секций (ГП, Инсоляция) сохраняем в контексте введённую пользователем average_salary
            if (calculationResult.updatedContext) {
              const updatedEstimate = {
                ...calculationResult.updatedContext.estimate,
                construction_design: allSectionsFromApi,
              };
              setCtxEstimateData(updatedEstimate);
              setCtxSectionDetails((prev) => {
                const apiSections =
                  calculationResult.updatedContext.sections || {};
                const merged = {};
                Object.keys(apiSections).forEach((sid) => {
                  const sectionIdNum = parseInt(sid, 10);
                  const fromApi = apiSections[sid];
                  const preserveSalary =
                    isUnifiedSection(sectionIdNum) &&
                    prev[sid]?.average_salary != null &&
                    Number(prev[sid].average_salary) > 0;
                  merged[sid] = preserveSalary
                    ? { ...fromApi, average_salary: prev[sid].average_salary }
                    : fromApi;
                });
                return merged;
              });
            }
          } else if (
            calculationResult.updatedContext?.estimate?.construction_design
          ) {
            // Fallback на updatedContext, если прямого ответа нет
            setIsDirectUpdate(true);
            lastCalculationAppliedRef.current = Date.now();
            const rawCtxMerged = [
              ...(calculationResult.updatedContext.estimate
                .construction_design || []),
              ...(calculationResult.updatedContext.estimate
                .construction_design_project || []),
            ];
            const seenCtxIds = new Set();
            const allSectionsFromCtx = rawCtxMerged.filter((s) => {
              const id = s?.section?.id;
              if (id == null) return true;
              if (seenCtxIds.has(id)) return false;
              seenCtxIds.add(id);
              return true;
            });
            setConstructionData((prev) => {
              const fromCtx = normalizeSectionsOrder(allSectionsFromCtx);
              const ctxIds = new Set(
                fromCtx.map((s) => s.section?.id).filter(Boolean),
              );
              const missingInCtx = (prev || []).filter(
                (s) => s.section?.id != null && !ctxIds.has(s.section.id),
              );
              return missingInCtx.length > 0
                ? normalizeSectionsOrder([...fromCtx, ...missingInCtx])
                : fromCtx;
            });

            // Обновляем контекст (для единых секций сохраняем average_salary из prev)
            if (calculationResult.updatedContext) {
              setCtxEstimateData(calculationResult.updatedContext.estimate);
              setCtxSectionDetails((prev) => {
                const apiSections =
                  calculationResult.updatedContext.sections || {};
                const merged = {};
                Object.keys(apiSections).forEach((sid) => {
                  const sectionIdNum = parseInt(sid, 10);
                  const fromApi = apiSections[sid];
                  const preserveSalary =
                    isUnifiedSection(sectionIdNum) &&
                    prev[sid]?.average_salary != null &&
                    Number(prev[sid].average_salary) > 0;
                  merged[sid] = preserveSalary
                    ? { ...fromApi, average_salary: prev[sid].average_salary }
                    : fromApi;
                });
                return merged;
              });
            }
          } else if (calculationResult.updatedContext) {
            // Если нет прямого ответа и нет updatedContext.estimate.construction_design,
            // просто обновляем контекст как есть (для единых секций сохраняем average_salary)
            setCtxEstimateData(calculationResult.updatedContext.estimate);
            setCtxSectionDetails((prev) => {
              const apiSections =
                calculationResult.updatedContext.sections || {};
              const merged = {};
              Object.keys(apiSections).forEach((sid) => {
                const sectionIdNum = parseInt(sid, 10);
                const fromApi = apiSections[sid];
                const preserveSalary =
                  isUnifiedSection(sectionIdNum) &&
                  prev[sid]?.average_salary != null &&
                  Number(prev[sid].average_salary) > 0;
                merged[sid] = preserveSalary
                  ? { ...fromApi, average_salary: prev[sid].average_salary }
                  : fromApi;
              });
              return merged;
            });
          }

          // ПРЯМОЕ ОБНОВЛЕНИЕ: Обновляем sectionDetails из результата калькуляции (блоки — сначала "Блок 1, 2, ...")
          // Но сохраняем total_day_efforts для Инсоляции, если оно было изменено пользователем
          // Для единых секций (ГП, Инсоляция) сохраняем average_salary из текущего состояния:
          // бэкенд считает по переданной ставке, но в ответе возвращает дефолт (400000), не перезатираем ввод пользователя
          if (calculationResult.updatedContext?.sections) {
            setSectionDetails((prev) => {
              const updatedSections = calculationResult.updatedContext.sections;
              const updated = {};
              Object.keys(updatedSections || {}).forEach((sid) => {
                const sectionIdNum = parseInt(sid, 10);
                const fromApi = updatedSections[sid];
                const preserveSalary =
                  isUnifiedSection(sectionIdNum) &&
                  prev[sid]?.average_salary != null &&
                  Number(prev[sid].average_salary) > 0;
                updated[sid] = {
                  ...fromApi,
                  average_salary: preserveSalary
                    ? prev[sid].average_salary
                    : fromApi.average_salary,
                  block_expenditures: sortBlocksFirst(
                    fromApi?.block_expenditures ?? [],
                  ),
                };
              });

              if (
                savedTotalDayEfforts !== null &&
                fieldName === "total_day_efforts"
              ) {
                updated[sectionId] = {
                  ...updated[sectionId],
                  total_day_efforts: savedTotalDayEfforts,
                };
              }

              return updated;
            });
          }

          // Обновляем данные шапки если они есть в результате
          if (
            calculationResult.contract_value_construction !== undefined &&
            estimateContext &&
            estimateContext.updateHeaderData
          ) {
            estimateContext.updateHeaderData({
              consent_price_construction:
                calculationResult.contract_value_construction,
              cost_price: calculationResult.cost_price,
              difference: calculationResult.difference,
            });
          }

          // Обновляем основную таблицу из per_section_data
          // per_section_data содержит актуальные данные для каждой секции после калькуляции
          // Используем его для обновления данных, даже если construction_design уже обновлен,
          // так как per_section_data может содержать более точные данные
          if (
            calculationResult.per_section_data &&
            calculationResult.per_section_data.length > 0
          ) {
            console.log("[GP Calculation] Обновляем из per_section_data:", {
              per_section_data: calculationResult.per_section_data,
            });
            setConstructionData((prev) => {
              const updated = prev.map((section) => {
                const updatedSectionData =
                  calculationResult.per_section_data.find(
                    (data) => data.section.id === section.section.id,
                  );

                if (updatedSectionData) {
                  const newSection = {
                    ...section,
                    day_efforts:
                      updatedSectionData.day_efforts ?? section.day_efforts,
                    money_expenditures:
                      updatedSectionData.money_expenditures ??
                      section.money_expenditures,
                    different_in_expenditures:
                      updatedSectionData.different_in_expenditures ??
                      section.different_in_expenditures,
                    base_price_per_m2:
                      updatedSectionData.base_price_per_m2 ??
                      section.base_price_per_m2,
                    expenditures_percent: {
                      ...section.expenditures_percent,
                      planned:
                        updatedSectionData.expenditures_percent?.planned ??
                        section.expenditures_percent.planned,
                      // Используем локальные изменения или данные от сервера
                      threshold:
                        localPercentChanges[section.section.id] !== undefined
                          ? localPercentChanges[section.section.id]
                          : (updatedSectionData.expenditures_percent
                              ?.threshold ??
                            section.expenditures_percent.threshold),
                    },
                    payroll_taxes:
                      updatedSectionData.payroll_taxes ?? section.payroll_taxes,
                    price_with_payroll_taxes:
                      updatedSectionData.price_with_payroll_taxes ??
                      section.price_with_payroll_taxes,
                  };

                  return newSection;
                }
                return section;
              });

              console.log(
                "[GP Calculation] Данные после обновления из per_section_data:",
                {
                  updated,
                  gpSection: updated.find((s) => s.section?.id === sectionId),
                },
              );

              return updated;
            });
          }

          // Обновляем детали секции если они есть в результате
          if (
            calculationResult.per_block_data &&
            calculationResult.per_block_data.length > 0
          ) {
            // Находим данные нашей секции в результате
            const updatedSectionData = calculationResult.per_block_data.find(
              (data) => data.section_id === sectionId,
            );

            if (updatedSectionData) {
              // Обновляем локальные данные секции с данными от сервера (сопоставление по tei_id)
              setSectionDetails((prev) => {
                const currentSection = prev[sectionId];
                const serverBlocks = updatedSectionData.blocks || [];
                const teiToServer = new Map(
                  serverBlocks.map((b) => [b.tei_id, b]),
                );

                // Агрегаты по секции (для распределения денег/бонусов между блоками)
                const sectionAggregate =
                  calculationResult.per_section_data?.find(
                    (s) => s.section?.id === sectionId,
                  );
                const totalBonusEfforts = sectionAggregate?.day_efforts?.bonus;
                const totalDirectMoney =
                  sectionAggregate?.money_expenditures?.direct;
                const totalBonusMoney =
                  sectionAggregate?.money_expenditures?.bonus;

                // Сумма прямых трудозатрат по блокам
                const editedIndex = fieldName.startsWith("block_efforts_")
                  ? parseInt(fieldName.split("_")[2])
                  : -1;
                const editedEfforts = parseFloat(value);

                const sumDirectEfforts = (
                  currentSection.block_expenditures || []
                )
                  .map((clientBlock, idx) => {
                    const teiId = clientBlock.block?.tei_id;
                    const serverBlock = teiToServer.get(teiId);
                    if (!serverBlock) return clientBlock.day_efforts;
                    return idx === editedIndex
                      ? editedEfforts
                      : serverBlock.day_efforts;
                  })
                  .reduce((a, b) => a + b, 0);

                const rebuiltBlocks = (
                  currentSection.block_expenditures || []
                ).map((clientBlock, index) => {
                  const teiId = clientBlock.block?.tei_id;
                  const serverBlock = teiToServer.get(teiId);
                  if (!serverBlock) return clientBlock;

                  const newDirectEfforts =
                    index === editedIndex
                      ? editedEfforts
                      : serverBlock.day_efforts;

                  const weight =
                    sumDirectEfforts > 0
                      ? newDirectEfforts / sumDirectEfforts
                      : 0;

                  return {
                    ...clientBlock,
                    day_efforts: newDirectEfforts,
                    bonus_day_efforts:
                      Math.round(totalBonusEfforts * weight * 100) / 100,
                    money_expenditures: Math.round(totalDirectMoney * weight),
                    bonus_money_expenditures: Math.round(
                      totalBonusMoney * weight,
                    ),
                  };
                });

                const updatedSection = {
                  ...currentSection,
                  average_salary:
                    updatedSectionData.average_salary ??
                    currentSection.average_salary,
                  bonus_percent:
                    updatedSectionData.bonus_percent ??
                    currentSection.bonus_percent,
                  block_expenditures: sortBlocksFirst(rebuiltBlocks),
                };

                const next = { ...prev, [sectionId]: updatedSection };
                // контекст обновится через отдельный useEffect
                return next;
              });
            }
          }

          // Принудительно обновляем таблицу
          setConstructionData((prev) => [...prev]);
        } catch (calcError) {
          // Ошибка при калькуляции секции
          setErrorMessage(
            "Ошибка при пересчете данных секции: " + calcError.message,
          );
          setShowErrorModal(true);
        } finally {
          // Убираем индикатор калькуляции
          setCalculatingFields((prev) => {
            const newState = { ...prev };
            delete newState[fieldKey];
            return newState;
          });
          setCalculatingSections((prev) => {
            const newSet = new Set(prev);
            newSet.delete(sectionId);
            return newSet;
          });
          setIsAnyCalculating(false);
        }

        // Убираем поле из редактирования
        setEditingFields((prev) => {
          const newState = { ...prev };
          delete newState[fieldKey];
          return newState;
        });
      } catch (error) {
        // Ошибка при сохранении поля
        setErrorMessage("Ошибка при сохранении данных: " + error.message);
        setShowErrorModal(true);
      } finally {
        setSavingFields((prev) => {
          const newState = { ...prev };
          delete newState[fieldKey];
          return newState;
        });
      }
    };

    // Новая функция для сохранения процента доли
    const handleSavePercentField = async (sectionId, value) => {
      // Блокируем сохранение полей во время общего сохранения
      if (isSaving) return;

      const fieldKey = `${sectionId}_threshold_percent`;

      try {
        setSavingFields((prev) => ({ ...prev, [fieldKey]: true }));
        // Показываем индикатор калькуляции
        setCalculatingFields((prev) => ({ ...prev, [fieldKey]: true }));
        setCalculatingSections((prev) => new Set(prev).add(sectionId));
        setIsAnyCalculating(true);
        // Сохраняем позицию скролла, чтобы после калькуляции страница не прыгала
        scrollPositionBeforeCalcRef.current =
          window.scrollY ?? document.documentElement.scrollTop;

        // Сохраняем локальное изменение
        const newValue = parseFloat(value);

        // Валидация: % доля должна быть > 0
        if (!(newValue > 0)) {
          // Очищаем все флаги состояния перед показом ошибки
          setSavingFields((prev) => {
            const newState = { ...prev };
            delete newState[fieldKey];
            return newState;
          });
          setCalculatingFields((prev) => {
            const newState = { ...prev };
            delete newState[fieldKey];
            return newState;
          });
          setCalculatingSections((prev) => {
            const newSet = new Set(prev);
            newSet.delete(sectionId);
            return newSet;
          });
          setIsAnyCalculating(false);

          // Отменяем режим редактирования, чтобы предотвратить повторное открытие попапа
          setEditingFields((prev) => {
            const newState = { ...prev };
            delete newState[fieldKey];
            return newState;
          });

          setErrorMessage("% доля раздела должна быть больше 0.");
          setShowErrorModal(true);
          return;
        }
        setLocalPercentChanges((prev) => ({
          ...prev,
          [sectionId]: newValue,
        }));

        // Обновляем данные в constructionData сразу
        setConstructionData((prev) => {
          const updated = prev.map((section) => {
            if (section.section.id === parseInt(sectionId)) {
              const updatedSection = {
                ...section,
                expenditures_percent: {
                  ...section.expenditures_percent,
                  threshold: newValue,
                },
              };

              return updatedSection;
            }
            return section;
          });

          return updated;
        });

        // Подготавливаем данные для измененной секции
        const sectionData = {
          threshold_percent: newValue,
          bonus_percent: globalBonusPercent, // Используем глобальный процент бонусов
          average_salary: sectionDetails[sectionId]?.average_salary,
          block_expenditures:
            sectionDetails[sectionId]?.block_expenditures || [],
        };

        // Вызываем калькуляцию для конкретной секции (которая автоматически пересчитает все)
        const calculationResult = await calculateSectionConstructionDesign(
          {
            estimate: estimateData,
            sections: sectionDetails,
          },
          projectId,
          parseInt(sectionId),
          sectionData,
          buildCalculatePayload({
            bonus_percent_construction_design: globalBonusPercent,
          }),
        );

        // СНАЧАЛА обновляем total_construction_design из результата калькуляции (приоритет прямому ответу API)
        if (calculationResult.total_construction_design) {
          setTotalConstructionDesign(
            calculationResult.total_construction_design,
          );
        } else if (
          calculationResult.updatedContext?.estimate?.total_construction_design
        ) {
          setTotalConstructionDesign(
            calculationResult.updatedContext.estimate.total_construction_design,
          );
        }

        // ПРЯМОЕ ОБНОВЛЕНИЕ: Берем данные из обновленного контекста, дедуплицируем по section.id (чтобы не задваивать ГП и др.)
        let dedupedConstructionDesign = null;
        if (calculationResult.updatedContext?.estimate?.construction_design) {
          setIsDirectUpdate(true);
          lastCalculationAppliedRef.current = Date.now();
          const rawFromCtx =
            calculationResult.updatedContext.estimate.construction_design || [];
          const seenCtxIds = new Set();
          dedupedConstructionDesign = rawFromCtx.filter((s) => {
            const id = s?.section?.id;
            if (id == null) return true;
            if (seenCtxIds.has(id)) return false;
            seenCtxIds.add(id);
            return true;
          });
          setConstructionData((prev) => {
            const normalized = normalizeSectionsOrder(
              dedupedConstructionDesign,
            );
            const ctxIds = new Set(
              normalized.map((s) => s.section?.id).filter(Boolean),
            );
            const missing = (prev || []).filter(
              (s) => s.section?.id != null && !ctxIds.has(s.section.id),
            );
            return missing.length > 0
              ? normalizeSectionsOrder([...normalized, ...missing])
              : normalized;
          });
        }

        // Обновляем контекст с результатами калькуляции; в контекст кладём уже дедуплицированный список
        // Для единых секций (ГП, Инсоляция) сохраняем average_salary из текущего контекста
        if (calculationResult.updatedContext) {
          const updatedEstimate = {
            ...calculationResult.updatedContext.estimate,
            total_construction_design:
              calculationResult.total_construction_design ||
              calculationResult.updatedContext.estimate
                .total_construction_design,
            ...(dedupedConstructionDesign != null && {
              construction_design: dedupedConstructionDesign,
            }),
          };
          setCtxEstimateData(updatedEstimate);
          setCtxSectionDetails((prev) => {
            const apiSections = calculationResult.updatedContext.sections || {};
            const merged = {};
            Object.keys(apiSections).forEach((sid) => {
              const sectionIdNum = parseInt(sid, 10);
              const fromApi = apiSections[sid];
              const preserveSalary =
                isUnifiedSection(sectionIdNum) &&
                prev[sid]?.average_salary != null &&
                Number(prev[sid].average_salary) > 0;
              merged[sid] = preserveSalary
                ? { ...fromApi, average_salary: prev[sid].average_salary }
                : fromApi;
            });
            return merged;
          });
        }

        if (
          !calculationResult.updatedContext?.estimate?.construction_design &&
          (calculationResult.construction_design ||
            calculationResult.construction_design_project)
        ) {
          setIsDirectUpdate(true);
          lastCalculationAppliedRef.current = Date.now();
          const rawAllFromApi = [
            ...(calculationResult.construction_design || []),
            ...(calculationResult.construction_design_project || []),
          ];
          const seenApiIds = new Set();
          const allFromApi = rawAllFromApi.filter((s) => {
            const id = s?.section?.id;
            if (id == null) return true;
            if (seenApiIds.has(id)) return false;
            seenApiIds.add(id);
            return true;
          });
          setConstructionData((prev) => {
            const normalized = normalizeSectionsOrder(allFromApi);
            const apiIds = new Set(
              normalized.map((s) => s.section?.id).filter(Boolean),
            );
            const missing = (prev || []).filter(
              (s) => s.section?.id != null && !apiIds.has(s.section.id),
            );
            return missing.length > 0
              ? normalizeSectionsOrder([...normalized, ...missing])
              : normalized;
          });
        }

        // ПРЯМОЕ ОБНОВЛЕНИЕ: Обновляем sectionDetails из результата калькуляции (блоки внутри секций — сначала "Блок 1, 2, ...")
        // Для единых секций (ГП, Инсоляция) сохраняем average_salary из текущего состояния
        if (calculationResult.updatedContext?.sections) {
          const sections = calculationResult.updatedContext.sections;
          setSectionDetails((prev) => {
            const sortedSections = {};
            Object.keys(sections || {}).forEach((sid) => {
              const sectionIdNum = parseInt(sid, 10);
              const fromApi = sections[sid];
              const preserveSalary =
                isUnifiedSection(sectionIdNum) &&
                prev[sid]?.average_salary != null &&
                Number(prev[sid].average_salary) > 0;
              sortedSections[sid] = {
                ...fromApi,
                average_salary: preserveSalary
                  ? prev[sid].average_salary
                  : fromApi.average_salary,
                block_expenditures: sortBlocksFirst(
                  fromApi?.block_expenditures ?? [],
                ),
              };
            });
            return sortedSections;
          });
        }

        // Обновляем шапку и основную таблицу из результата калькуляции
        if (
          calculationResult.contract_value_construction !== undefined &&
          estimateContext &&
          estimateContext.updateHeaderData
        ) {
          estimateContext.updateHeaderData(calculationResult);
        }

        // Обновляем основную таблицу из per_section_data
        if (
          calculationResult.per_section_data &&
          calculationResult.per_section_data.length > 0
        ) {
          setConstructionData((prev) => {
            const updated = prev.map((section) => {
              const updatedSectionData =
                calculationResult.per_section_data.find(
                  (data) => data.section.id === section.section.id,
                );

              if (updatedSectionData) {
                const newSection = {
                  ...section,
                  day_efforts:
                    updatedSectionData.day_efforts ?? section.day_efforts,
                  money_expenditures:
                    updatedSectionData.money_expenditures ??
                    section.money_expenditures,
                  different_in_expenditures:
                    updatedSectionData.different_in_expenditures ??
                    section.different_in_expenditures,
                  base_price_per_m2:
                    updatedSectionData.base_price_per_m2 ??
                    section.base_price_per_m2,
                  expenditures_percent: {
                    ...section.expenditures_percent,
                    // Используем локальные изменения или данные от сервера
                    threshold:
                      localPercentChanges[section.section.id] !== undefined
                        ? localPercentChanges[section.section.id]
                        : (updatedSectionData.expenditures_percent?.threshold ??
                          section.expenditures_percent.threshold),
                  },
                };

                return newSection;
              }
              return section;
            });

            return updated;
          });
        }

        // Обновляем детали секций из per_block_data
        if (
          calculationResult.per_block_data &&
          calculationResult.per_block_data.length > 0
        ) {
          setSectionDetails((prev) => {
            const updated = { ...prev };
            const currentSectionId = sectionId; // Сохраняем sectionId из параметров функции

            calculationResult.per_block_data.forEach((blockData) => {
              const sid = blockData.section_id;

              if (updated[sid]) {
                updated[sid] = {
                  ...updated[sid],
                  average_salary:
                    blockData.average_salary || updated[sid].average_salary,
                  // Сохраняем bonus_percent: используем значение из blockData только если оно явно задано,
                  // иначе сохраняем текущее значение или глобальный процент бонусов
                  bonus_percent:
                    blockData.bonus_percent !== undefined &&
                    blockData.bonus_percent !== null
                      ? blockData.bonus_percent
                      : (updated[sid].bonus_percent ?? globalBonusPercent),
                  // Используем наше измененное значение threshold_percent для изменяемой секции
                  threshold_percent:
                    sid === currentSectionId
                      ? newValue
                      : blockData.threshold_percent ||
                        updated[sid].threshold_percent ||
                        0,
                  block_expenditures: sortBlocksFirst(
                    blockData.blocks?.map((block, index) => {
                      const currentBlock =
                        updated[sid].block_expenditures?.[index];
                      if (currentBlock) {
                        return {
                          ...currentBlock,
                          day_efforts:
                            block.day_efforts || currentBlock.day_efforts,
                          bonus_day_efforts:
                            block.bonus_day_efforts ||
                            currentBlock.bonus_day_efforts,
                          money_expenditures:
                            block.money_expenditures ||
                            currentBlock.money_expenditures,
                          bonus_money_expenditures:
                            block.bonus_money_expenditures ||
                            currentBlock.bonus_money_expenditures,
                        };
                      }
                      return currentBlock;
                    }) ||
                      updated[sid].block_expenditures ||
                      [],
                  ),
                };
              }
            });

            // контекст обновится через отдельный useEffect
            return updated;
          });
        }

        // Принудительно обновляем таблицу
        setConstructionData((prev) => [...prev]);

        // Убираем поле из редактирования
        setEditingFields((prev) => {
          const newState = { ...prev };
          delete newState[fieldKey];
          return newState;
        });
      } catch (error) {
        // Ошибка при сохранении процента
        setErrorMessage("Ошибка при сохранении данных: " + error.message);
        setShowErrorModal(true);
      } finally {
        // Убираем индикатор калькуляции
        setCalculatingFields((prev) => {
          const newState = { ...prev };
          delete newState[fieldKey];
          return newState;
        });
        setCalculatingSections((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sectionId);
          return newSet;
        });
        setIsAnyCalculating(false);

        setSavingFields((prev) => {
          const newState = { ...prev };
          delete newState[fieldKey];
          return newState;
        });
      }
    };

    const handleCancelEdit = (sectionId, fieldName) => {
      // Блокируем отмену редактирования во время сохранения
      if (isSaving) return;

      const fieldKey = `${sectionId}_${fieldName}`;

      // Если отменяем редактирование процента, очищаем локальное изменение
      if (fieldName === "threshold_percent") {
        setLocalPercentChanges((prev) => {
          const newState = { ...prev };
          delete newState[sectionId];
          return newState;
        });
      }

      setEditingFields((prev) => {
        const newState = { ...prev };
        delete newState[fieldKey];
        return newState;
      });
    };

    // Функция для подготовки данных для калькуляции одной секции
    const prepareSectionCalculationData = (sectionId, fieldName, newValue) => {
      // Находим детали секции
      const currentSectionDetails = sectionDetails[sectionId];
      if (!currentSectionDetails) {
        // Детали секции не найдены для ID
        return null;
      }

      // Находим данные секции из constructionData
      const constructionSection = constructionData.find(
        (item) => item.section.id === sectionId,
      );
      if (!constructionSection) {
        // Данные секции не найдены в constructionData для ID
        return null;
      }

      // Проверяем, является ли раздел единым (без блоков)
      const isInsolation = isInsolationSection(sectionId);
      const isGP = isGPSection(sectionId);
      const isUnified = isInsolation || isGP;

      // Для единых разделов (без блоков) используем упрощенную структуру данных
      if (isUnified) {
        const sectionData = {
          bonus_percent: Math.max(globalBonusPercent, 0),
          average_salary: Math.max(currentSectionDetails.average_salary, 1),
          block_expenditures: [], // Пустой массив для единых разделов
        };

        // Для "Инсоляция" добавляем total_day_efforts
        // Если поле изменяется, используем новое значение, иначе берем из currentSectionDetails
        if (isInsolation) {
          if (fieldName === "total_day_efforts") {
            // Используем новое значение, которое передается в функцию
            sectionData.total_day_efforts = Math.max(parseFloat(newValue), 0);
          } else {
            // Используем текущее значение из sectionDetails
            sectionData.total_day_efforts = Math.max(
              safeNumber(currentSectionDetails.total_day_efforts, 0),
              0,
            );
          }
        }

        // Обновляем значение в зависимости от типа поля
        if (fieldName === "average_salary") {
          sectionData.average_salary = parseFloat(newValue);
        } else if (fieldName === "bonus_percent") {
          sectionData.bonus_percent = parseFloat(newValue);
        }

        // Проверяем, что обязательные поля не отрицательные (0 для bonus_percent допустим)
        if (sectionData.bonus_percent < 0) {
          throw new Error("bonus_percent не может быть отрицательным");
        }
        if (sectionData.average_salary <= 0) {
          throw new Error("average_salary должен быть больше 0");
        }

        // Дополнительная проверка и исправление значений (0 для бонусов допустим)
        sectionData.bonus_percent = Math.max(sectionData.bonus_percent, 0);
        sectionData.average_salary = Math.max(sectionData.average_salary, 1);

        return sectionData;
      }

      // Обычная логика для остальных разделов
      // Получаем tei_id из деталей конкретной секции
      const sectionTeiIds = currentSectionDetails.block_expenditures?.map(
        (block) => block.block.tei_id,
      ) || [15, 16, 33, 31]; // Fallback если детали не загружены

      // Подготавливаем данные для секции в новом формате
      const sectionData = {
        bonus_percent: Math.max(globalBonusPercent, 0), // Используем глобальный процент бонусов (0 допустим)
        average_salary: Math.max(currentSectionDetails.average_salary, 1), // Минимум 1
        block_expenditures:
          currentSectionDetails.block_expenditures?.map(
            (block, blockIndex) => ({
              block: {
                id: block.block?.id,
                name: block.block?.name || "",
                tei_id: block.block?.tei_id || sectionTeiIds[blockIndex],
              },
              floor_count: block.floor_count,
              day_efforts:
                fieldName === `block_efforts_${blockIndex}`
                  ? Math.max(parseFloat(newValue), 0)
                  : Math.max(safeNumber(block.day_efforts, 0), 0),
              bonus_day_efforts: safeNumber(block.bonus_day_efforts, 0),
              money_expenditures: safeNumber(block.money_expenditures, 0),
              bonus_money_expenditures: safeNumber(
                block.bonus_money_expenditures,
                0,
              ),
            }),
          ) || [],
      };

      // Обновляем значение в зависимости от типа поля
      if (fieldName === "average_salary") {
        sectionData.average_salary = parseFloat(newValue);
      } else if (fieldName === "bonus_percent") {
        sectionData.bonus_percent = parseFloat(newValue);
      } else if (fieldName.startsWith("block_efforts_")) {
        const blockIndex = parseInt(fieldName.split("_")[2]);
        if (sectionData.block_expenditures[blockIndex]) {
          sectionData.block_expenditures[blockIndex].day_efforts =
            parseFloat(newValue);
        }
      }

      // Проверяем, что обязательные поля не отрицательные (0 для bonus_percent допустим)
      if (sectionData.bonus_percent < 0) {
        throw new Error("bonus_percent не может быть отрицательным");
      }
      if (sectionData.average_salary <= 0) {
        throw new Error("average_salary должен быть больше 0");
      }

      // Дополнительная проверка и исправление значений (0 для бонусов допустим)
      sectionData.bonus_percent = Math.max(sectionData.bonus_percent, 0);
      sectionData.average_salary = Math.max(sectionData.average_salary, 1);

      return sectionData;
    };

    // удалено: prepareSaveData — заменено на buildSavePayload из контекста
    // удалено: handleSaveAll — больше не используется

    // Функция для сохранения всех измененных секций
    const saveAllModifiedSections = async () => {
      try {
        // Подготавливаем данные для сохранения всех секций
        // удалено: предварительный конструктор constructionDesignData — используем buildSavePayload()

        // Используем только локальные данные без запросов к базе
        // Обновляем bonus_percent во всех секциях на актуальное значение
        const updatedSectionDetails = Object.fromEntries(
          Object.entries(sectionDetails).map(([sid, data]) => [
            sid,
            { ...data, bonus_percent: globalBonusPercent },
          ]),
        );

        // Обновляем контекст с новыми данными секций
        setCtxSectionDetails(updatedSectionDetails);

        // saveConstructionDesignPreservingData сам строит payload используя buildCalculationPayload

        // Сохраняем данные в базу
        // saveConstructionDesignPreservingData использует buildCalculationPayload внутри себя
        await saveConstructionDesignPreservingData(
          projectId,
          estimateData,
          updatedSectionDetails,
        );

        // Обновляем детали секций из одного GET сметы (section_details)
        try {
          const response = await getConstructionDesign(projectId);
          const sectionDetailsFromApi = response.section_details || {};
          const updatedSectionDetails = Object.fromEntries(
            Object.entries(sectionDetailsFromApi).map(([sid, det]) => [
              sid,
              {
                ...det,
                section_id: parseInt(sid, 10),
                block_expenditures: sortBlocksFirst(
                  det?.block_expenditures ?? [],
                ),
              },
            ]),
          );
          setSectionDetails(updatedSectionDetails);
        } catch (err) {
          // Ошибка при обновлении деталей секций
        }

        // Очищаем локальные изменения после успешного сохранения
        setLocalPercentChanges({});

        return { success: true };
      } catch (error) {
        // Ошибка при сохранении секций
        throw error;
      }
    };

    // Функция для передачи данных в Estimate
    const handleSaveForEstimate = async () => {
      // Блокируем сохранение для Estimate во время общего сохранения
      if (isSaving) return;

      try {
        // Блокируем сохранение, если есть незаполненные разделы
        if (incompleteSectionIds.length > 0) {
          const incompleteNames = incompleteSectionIds
            .map((id) => {
              const section = constructionData.find(
                (s) => s.section?.id === id,
              );
              return section?.section?.name || `Раздел ${id}`;
            })
            .join(", ");
          setErrorMessage(
            `Заполните обязательные поля в ${incompleteSectionIds.length} раздел(а/ов): ${incompleteNames}. Для обычных разделов требуется средняя ставка и хотя бы один блок, для раздела "Инсоляция" требуется средняя ставка и трудозатраты в днях, для раздела "ГП" требуется только средняя ставка на отдел.`,
          );
          setShowErrorModal(true);
          return;
        }
        // Сначала сохраняем все измененные секции
        await saveAllModifiedSections();

        // Получаем текущие данные проекта после сохранения
        const currentData = await getConstructionDesign(projectId);

        // Обновляем total_construction_design из текущих данных
        if (currentData.total_construction_design) {
          setTotalConstructionDesign(currentData.total_construction_design);
        }

        // Подготавливаем данные для калькуляции всех секций на базе АКТУАЛЬНЫХ данных (порядок = order_index)
        const calculationData = {
          per_section_data: currentData.sections.map((section, order_index) => {
            const finalThresholdPercent =
              localPercentChanges[section.section.id] !== undefined
                ? localPercentChanges[section.section.id]
                : section.expenditures_percent?.threshold;

            return {
              section: {
                id: section.section.id,
                name: section.section.name,
                ...(section.section.description != null && {
                  description: section.section.description,
                }),
              },
              day_efforts: section.day_efforts,
              money_expenditures: section.money_expenditures,
              different_in_expenditures: section.different_in_expenditures,
              base_price_per_m2: section.base_price_per_m2,
              expenditures_percent: {
                ...section.expenditures_percent,
                threshold: finalThresholdPercent,
              },
              payroll_taxes:
                section.payroll_taxes || section.money_expenditures?.direct,
              price_with_payroll_taxes:
                section.price_with_payroll_taxes ||
                section.money_expenditures?.threshold +
                  section.money_expenditures?.direct,
              order_index,
            };
          }),
          per_block_data: currentData.sections.map((section, order_index) => {
            const sectionDetail = sectionDetails[section.section.id];
            const finalThresholdPercent =
              localPercentChanges[section.section.id] !== undefined
                ? localPercentChanges[section.section.id]
                : section.expenditures_percent?.threshold;

            return {
              section_id: section.section.id,
              threshold_percent: finalThresholdPercent,
              average_salary: sectionDetail?.average_salary,
              blocks:
                sectionDetail?.block_expenditures?.map((block) => ({
                  tei_id: block.block?.tei_id,
                  day_efforts: block.day_efforts,
                })) || [],
              order_index,
            };
          }),
          // Карта «имя раздела» → id для подстановки section_id (API часто не возвращает section_id в ролях)
          ...(function () {
            const sectionsByName = new Map();
            const add = (arr) =>
              (arr || []).forEach((item) => {
                const id = item?.section?.id;
                const name = item?.section?.name?.trim();
                if (id != null && name != null) sectionsByName.set(name, id);
              });
            add(currentData.construction_design);
            add(currentData.construction_design_project);
            add(currentData.sections);
            const bimSectionId = (r) => {
              if (r.section_id != null) return r.section_id;
              const name = r.role?.trim();
              if (!name?.startsWith("BIM ")) return null;
              return (
                sectionsByName.get(name.replace(/^BIM\s+/, "").trim()) ?? null
              );
            };
            const qualitySectionId = (r) => {
              if (r.section_id != null) return r.section_id;
              const n = r.role?.trim();
              if (
                !n ||
                n === "РО 5" ||
                n === "Норм Контроль" ||
                n === "Норм.контроль"
              )
                return null;
              return sectionsByName.get(n) ?? null;
            };
            const deliverySectionId = (r) =>
              r.section_id != null
                ? r.section_id
                : (sectionsByName.get(r.role?.trim()) ?? null);
            return {
              bim_coordination: {
                include_bonuses: true,
                average_salary:
                  estimateContext?.bimCoordinationData?.average_salary ||
                  currentData.bim_coordination?.average_salary,
                quality_control_percent:
                  estimateContext?.bimCoordinationData
                    ?.quality_control_percent ||
                  currentData.bim_coordination?.quality_control_percent,
                direct_bonus_percent:
                  estimateContext?.bimCoordinationData?.direct_bonus_percent ||
                  currentData.bim_coordination?.direct_bonus_percent,
                bim_role_salaries: (
                  estimateContext?.bimCoordinationData?.bim_role_salaries ||
                  currentData.bim_coordination?.bim_role_salaries ||
                  currentData.bim_coordination?.roles ||
                  []
                ).map((r) => ({
                  role: r.role,
                  average_salary: r.average_salary ?? 0,
                  section_id: bimSectionId(r),
                })),
              },
              quality_control: {
                include_bonuses: true,
                average_salary:
                  estimateContext?.qualityControlData?.average_salary ||
                  currentData.quality_control?.average_salary,
                quality_control_percent:
                  estimateContext?.qualityControlData
                    ?.quality_control_percent ||
                  currentData.quality_control?.quality_control_percent,
                direct_bonus_percent:
                  estimateContext?.qualityControlData?.direct_bonus_percent ||
                  currentData.quality_control?.direct_bonus_percent,
                normative_control_efforts:
                  estimateContext?.qualityControlData
                    ?.normative_control_efforts ??
                  currentData.quality_control?.normative_control_efforts ??
                  currentData.quality_control?.roles?.find(
                    (role) => role.role === "Норм Контроль",
                  )?.day_efforts?.direct,
                quality_control_role_salaries: (
                  estimateContext?.qualityControlData
                    ?.quality_control_role_salaries ||
                  currentData.quality_control?.quality_control_role_salaries ||
                  currentData.quality_control?.roles ||
                  []
                ).map((r) => {
                  const isNorm =
                    r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
                  return {
                    role: r.role,
                    average_salary: isNorm ? (r.average_salary ?? null) : null,
                    section_id: qualitySectionId(r),
                  };
                }),
              },
              project_delivery: {
                include_bonuses: true,
                average_salary:
                  estimateContext?.projectDeliveryData?.average_salary ||
                  currentData.project_delivery?.average_salary,
                quality_control_percent:
                  estimateContext?.projectDeliveryData
                    ?.quality_control_percent ||
                  currentData.project_delivery?.quality_control_percent,
                direct_bonus_percent:
                  estimateContext?.projectDeliveryData?.direct_bonus_percent ||
                  currentData.project_delivery?.direct_bonus_percent,
                project_delivery_role_salaries: (
                  estimateContext?.projectDeliveryData
                    ?.project_delivery_role_salaries ||
                  currentData.project_delivery
                    ?.project_delivery_role_salaries ||
                  currentData.project_delivery?.roles ||
                  []
                ).map((r) => ({
                  role: r.role,
                  average_salary: r.average_salary ?? 0,
                  section_id: deliverySectionId(r),
                })),
              },
            };
          })(),
          project_management: {
            include_bonuses: true,
            quality_control_percent:
              estimateContext?.projectManagementData?.quality_control_percent ||
              currentData.project_management?.quality_control_percent,
            direct_bonus_percent:
              estimateContext?.projectManagementData?.direct_bonus_percent ||
              currentData.project_management?.direct_bonus_percent,
            administration_efforts:
              estimateContext?.projectManagementData?.administration_efforts ??
              currentData.project_management?.administration_efforts ??
              currentData.project_management?.roles?.find(
                (role) => role.role === "Администрирование Проекта",
              )?.day_efforts?.direct,
            management_role_salaries:
              estimateContext?.projectManagementData
                ?.management_role_salaries ||
              currentData.project_management?.management_role_salaries ||
              [],
          },
          total_area: currentData.total_area,
          contract_value: currentData.contract_value,
          contract_value_construction: currentData.contract_value_construction,
          contract_value_before_nds: currentData.contract_value_before_nds,
          cost_price: currentData.cost_price,
          difference: currentData.difference,
          // Обязательное поле для API
          bonus_percent_construction_design: globalBonusPercent,
        };

        // Калькуляция уже была выполнена в saveEditGlobalBonus
        // Не нужно вызывать performCalculation, так как он перезагружает данные с сервера

        // Очищаем локальные изменения после успешного сохранения
        setLocalPercentChanges({});
      } catch (error) {
        // Ошибка при сохранении для Estimate
        throw error;
      }
    };

    // Компонент модала удаления
    const DeleteModal = ({ open, onClose, onConfirm, sectionName }) => {
      if (!open) return null;

      return (
        <>
          <div
            className="approval-modal-backdrop"
            onClick={isSaving ? undefined : onClose}
          />
          <div className="approval-modal">
            <div className="approval-modal-title">Удаление раздела</div>
            <div className="approval-modal-warning">
              <span className="approval-modal-warning-icon">⚠️</span>
              <span>
                Вы уверены, что хотите удалить раздел "{sectionName}"? Это
                действие нельзя будет отменить.
              </span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn outline"
                onClick={onClose}
                disabled={isSaving}
                style={{
                  opacity: isSaving ? 0.6 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className="approval-btn"
                onClick={onConfirm}
                disabled={isSaving}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  opacity: isSaving ? 0.6 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                Удалить
              </button>
            </div>
          </div>
        </>
      );
    };

    // Компонент модала успеха
    const SuccessModal = ({ open, onClose, message }) => {
      if (!open) return null;

      return (
        <>
          <div
            className="approval-modal-backdrop"
            onClick={isSaving ? undefined : onClose}
          />
          <div className="approval-modal">
            <div className="approval-modal-title">Успешно!</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#f0f8f4",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#226a3a",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>✅</span>
              <span>{message}</span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn green"
                onClick={onClose}
                disabled={isSaving}
                style={{
                  opacity: isSaving ? 0.6 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      );
    };

    // Компонент модала ошибки
    const ErrorModal = ({ open, onClose, message }) => {
      if (!open) return null;

      return (
        <>
          <div
            className="approval-modal-backdrop"
            onClick={isSaving ? undefined : onClose}
          />
          <div className="approval-modal">
            <div className="approval-modal-title">Ошибка</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#fff8f1",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#b26a00",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>⚠️</span>
              <span>{message}</span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn outline"
                onClick={onClose}
                disabled={isSaving}
                style={{
                  opacity: isSaving ? 0.6 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      );
    };

    // Компонент для редактируемого поля процента (threshold) в общем списке
    const EditablePercentField = ({
      sectionId,
      value,
      onSave,
      onCancel,
      isEditing,
      onEdit,
      isSaving,
      isCalculating,
    }) => {
      const EDIT_CELL_WIDTH = 120;
      const inputRef = React.useRef(null);
      const hasLocalChanges = localPercentChanges[sectionId] !== undefined;
      const [localValue, setLocalValue] = useState(
        value === undefined || value === null ? "" : String(value),
      );

      const computeWidthPx = (val) => {
        const text =
          val === undefined || val === null || val === "" ? "0" : String(val);
        const charCount = text.length;
        const w = Math.max(30, charCount * 10 + 16);
        return Math.min(EDIT_CELL_WIDTH, w);
      };

      useEffect(() => {
        setLocalValue(
          value === undefined || value === null ? "" : String(value),
        );
      }, [value]);

      useEffect(() => {
        if (isEditing && inputRef.current) {
          inputRef.current.focus();
          const input = inputRef.current;
          requestAnimationFrame(() => {
            try {
              input.select();
            } catch {}
          });
        }
      }, [isEditing]);

      const currentValue = isEditing ? localValue : value;

      if (isEditing) {
        const w = percentColWidth;
        return (
          <div
            style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
          >
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              inputMode="decimal"
              value={currentValue}
              ref={inputRef}
              onChange={(e) => {
                const raw = e.target.value;
                const sanitized = raw
                  .replace(/,/g, ".")
                  .replace(/[^0-9.]/g, "")
                  .replace(/(\..*)\./g, "$1");
                setLocalValue(sanitized);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSave(sectionId, localValue);
                } else if (e.key === "Escape") {
                  setLocalValue(
                    value === undefined || value === null ? "" : String(value),
                  );
                  onCancel(sectionId, "threshold_percent");
                }
              }}
              style={{
                width: `${w}px`,
                padding: "6px 10px",
                border: "1.5px dashed #B8C2D1",
                borderRadius: "6px",
                background: "#FFFFFF",
                fontSize: "14px",
                textAlign: "center",
                outline: "none",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#007bff")}
              onBlur={(e) => {
                e.target.style.borderColor = "#ccc";
                // Проверяем значение перед сохранением
                const numValue = parseFloat(localValue);
                // Если значение невалидное (<= 0 или пустое), отменяем редактирование вместо сохранения
                if (
                  !localValue ||
                  localValue === "" ||
                  isNaN(numValue) ||
                  numValue <= 0
                ) {
                  onCancel(sectionId, "threshold_percent");
                } else {
                  // Автоматически сохраняем при потере фокуса
                  onSave(sectionId, localValue);
                }
              }}
              disabled={!isEditable || isSaving || isCalculating}
            />
          </div>
        );
      }

      const wView = percentColWidth;
      return (
        <div
          style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
        >
          <span
            style={{
              fontSize: "14px",
              display: "inline-block",
              width: `${wView}px`,
              cursor: !isEditable || isSaving ? "default" : "pointer",
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: hasLocalChanges ? "#EFF6FF" : "#F9FBFF",
              color: hasLocalChanges ? "#0B5ED7" : "#1F2937",
            }}
            onClick={() =>
              !isEditable || isSaving
                ? undefined
                : onEdit(sectionId, "threshold_percent")
            }
          >
            {typeof value === "number" ? value.toFixed(2) : value}
          </span>
        </div>
      );
    };

    // Функция для форматирования чисел: максимум 2 знака после запятой, без лишних нулей
    const formatNumber = (num) => {
      if (num === null || num === undefined || isNaN(num)) return "0";
      // Используем toFixed для точного округления до 2 знаков, затем убираем лишние нули
      const fixed = Number(num).toFixed(2);
      // Убираем лишние нули в конце (но оставляем точку, если есть дробная часть)
      return fixed.replace(/\.?0+$/, "");
    };

    // Функция для преобразования данных из API в формат для отображения
    const transformApiData = (data) => {
      const result = data.map((item) => {
        // Проверяем, есть ли детали для этой секции
        const details = sectionDetails[item.section.id];

        // Проверяем, является ли раздел единым (без блоков)
        const sectionId = item.section?.id;
        const isUnified = isUnifiedSection(sectionId);

        // Всегда используем значения из API напрямую для отображения
        // Это гарантирует, что мы показываем именно то, что пришло с бэкенда
        let dayEfforts = item.day_efforts;
        let moneyExpenditures = item.money_expenditures;

        // Для единых секций (Инсоляция, ГП) всегда используем значения из API
        if (isUnified) {
          // Используем значения напрямую из API
          dayEfforts = {
            direct: item.day_efforts?.direct ?? 0,
            bonus: item.day_efforts?.bonus ?? 0,
            total: item.day_efforts?.total ?? 0,
          };

          moneyExpenditures = {
            direct: item.money_expenditures?.direct ?? 0,
            bonus: item.money_expenditures?.bonus ?? 0,
            threshold: item.money_expenditures?.threshold ?? 0,
            planned: item.money_expenditures?.planned ?? 0,
          };
        } else if (details && details.block_expenditures) {
          // Для секций с блоками вычисляем из блоков
          // Рассчитываем общие трудозатраты из деталей для внутренних расчетов
          const totalDayEfforts = details.block_expenditures.reduce(
            (sum, block) => {
              return sum + block.day_efforts + block.bonus_day_efforts;
            },
            0,
          );

          const totalMoneyExpenditures = details.block_expenditures.reduce(
            (sum, block) => {
              return (
                sum + block.money_expenditures + block.bonus_money_expenditures
              );
            },
            0,
          );

          // Для отображения всегда используем значения из API, если они есть
          // Пересчет из блоков используется только для внутренних расчетов
          if (
            item.day_efforts &&
            (item.day_efforts.direct !== undefined ||
              item.day_efforts.bonus !== undefined)
          ) {
            // Используем значения из API напрямую
            dayEfforts = {
              direct: item.day_efforts.direct ?? 0,
              bonus: item.day_efforts.bonus ?? 0,
              total: item.day_efforts.total ?? totalDayEfforts,
            };
          } else {
            // Если значений из API нет, пересчитываем из блоков
            dayEfforts = {
              direct: details.block_expenditures.reduce(
                (sum, block) => sum + block.day_efforts,
                0,
              ),
              bonus: details.block_expenditures.reduce(
                (sum, block) => sum + block.bonus_day_efforts,
                0,
              ),
              total: totalDayEfforts,
            };
          }

          moneyExpenditures = {
            direct: details.block_expenditures.reduce(
              (sum, block) => sum + block.money_expenditures,
              0,
            ),
            bonus: details.block_expenditures.reduce(
              (sum, block) => sum + block.bonus_money_expenditures,
              0,
            ),
            threshold: item.money_expenditures.threshold,
            planned: totalMoneyExpenditures,
          };
        }

        return {
          section: item.section.name,
          sectionDescription: item.section?.description ?? "",
          sectionId: item.section.id,
          percent:
            localPercentChanges[item.section.id] !== undefined
              ? localPercentChanges[item.section.id]
              : item.expenditures_percent.threshold,
          forecast: item.money_expenditures.threshold,
          costWithTaxes:
            item.price_with_payroll_taxes ??
            (item.money_expenditures.threshold ?? 0) +
              (item.money_expenditures.direct ?? 0),
          plan: moneyExpenditures.planned,
          percentLabor: item.expenditures_percent.planned.toString(),
          deviation: item.different_in_expenditures,
          payrollTaxes:
            item.payroll_taxes ?? item.money_expenditures.direct ?? 0,
          pricePerM2: item.base_price_per_m2,
          bonusDirect: `${formatNumber(dayEfforts.direct)} дн\n${formatNumber(
            dayEfforts.bonus,
          )} дн`,
          bonusFund: `${moneyExpenditures.direct.toLocaleString()}\n${moneyExpenditures.bonus.toLocaleString()}`,
        };
      });

      return result;
    };

    // Преобразуем данные из total_construction_design с бэкенда в формат totals
    const getTotalsFromBackend = (totalData) => {
      if (!totalData) {
        return null;
      }

      const me = totalData.money_expenditures || {};
      const de = totalData.day_efforts || {};
      const ep = totalData.expenditures_percent || {};

      return {
        forecast: me.threshold || 0,
        costWithTaxes: totalData.price_with_payroll_taxes || 0,
        plan: me.planned || 0,
        deviation: totalData.different_in_expenditures || 0,
        payrollTaxes: totalData.payroll_taxes || 0,
        bonusFund: `${(me.direct || 0).toLocaleString()}\n${(
          me.bonus || 0
        ).toLocaleString()}`,
        bonusDirect: `${formatNumber((de.direct || 0) + (de.bonus || 0))} дн`,
        percentTotal: ep.threshold || 0,
        percentLaborTotal: ep.planned || 0,
      };
    };

    const renderExpandedContent = (idx) => {
      const sectionId = constructionData[idx]?.section?.id;
      const details = sectionDetails[sectionId];
      const isLoading = loadingDetails[idx];
      const isCalculating = calculatingSections.has(sectionId);

      if (isLoading) {
        return <div>Загрузка деталей...</div>;
      }

      if (isCalculating) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              color: "#666",
              fontSize: "14px",
            }}
          >
            <div
              style={{
                display: "inline-block",
                width: "20px",
                height: "20px",
                border: "2px solid #f3f3f3",
                borderTop: "2px solid #007bff",
                borderRadius: "50%",
                marginRight: "10px",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
            Выполняется пересчет данных...
          </div>
        );
      }

      if (!details) {
        return <div>Нет данных</div>;
      }

      // Компонент для редактируемого поля
      const EditableField = ({
        fieldName,
        value,
        isCurrency = false,
        isPercent = false,
        onSave,
        onCancel,
        isEditing,
        onEdit,
        isSaving,
        isCalculating,
      }) => {
        // fieldKey не используется ниже — оставлен только localValue
        const [localValue, setLocalValue] = useState(
          value === undefined || value === null ? "" : String(value),
        );
        const inputRef = React.useRef(null);
        const originalValueRef = React.useRef(value);

        // Функция для расчёта динамической ширины (минимум 30px)
        const computeWidthPx = (val) => {
          const text =
            val === undefined || val === null || val === "" ? "0" : String(val);
          const charCount = text.length;
          return Math.max(30, charCount * 10 + 16); // 10px/символ + паддинги
        };

        // Обновляем локальное значение при изменении props
        useEffect(() => {
          setLocalValue(
            value === undefined || value === null ? "" : String(value),
          );
        }, [value]);

        // Сохраняем исходное значение при входе в режим редактирования
        useEffect(() => {
          if (isEditing) {
            originalValueRef.current = value;
          }
        }, [isEditing, value]);

        // Автофокус и выделение текста при входе в режим редактирования
        useEffect(() => {
          if (isEditing && inputRef.current) {
            inputRef.current.focus();
            const input = inputRef.current;
            requestAnimationFrame(() => {
              try {
                input.select();
              } catch {}
            });
          }
        }, [isEditing]);

        const currentValue = isEditing ? localValue : value;

        if (isEditing) {
          const w = computeWidthPx(currentValue);
          return (
            <div
              style={{
                position: "relative",
                width: `${w}px`,
                margin: "0 auto",
              }}
            >
              <input
                type="number"
                step="1"
                min="0"
                inputMode="numeric"
                value={currentValue}
                ref={inputRef}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Разрешаем только цифры и одну точку/запятую, убираем остальные символы
                  const sanitized = raw
                    .replace(/,/g, ".")
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1");
                  setLocalValue(sanitized);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onSave(sectionId, fieldName, localValue);
                  } else if (e.key === "Escape") {
                    setLocalValue(value); // Сбрасываем к исходному значению
                    onCancel(sectionId, fieldName);
                  }
                }}
                style={{
                  width: `${w}px`,
                  padding: "6px 10px",
                  border: "1.5px dashed #B8C2D1",
                  borderRadius: "6px",
                  background: "#FFFFFF",
                  fontSize: "14px",
                  textAlign: "center",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ccc";

                  // Проверяем, изменилось ли значение от исходного
                  const originalValue = originalValueRef.current;
                  const originalStr =
                    originalValue === undefined || originalValue === null
                      ? ""
                      : String(originalValue);
                  const currentStr = localValue;

                  // Если значение не изменилось — просто отменяем редактирование без сохранения
                  if (originalStr === currentStr) {
                    onCancel(sectionId, fieldName);
                    return;
                  }

                  // Если значение пустое или некорректное — восстанавливаем исходное
                  const numValue = parseFloat(localValue);
                  if (localValue === "" || Number.isNaN(numValue)) {
                    setLocalValue(originalStr);
                    onCancel(sectionId, fieldName);
                    return;
                  }

                  // Автоматически сохраняем при потере фокуса только если значение валидно и изменилось
                  onSave(sectionId, fieldName, localValue);
                }}
                disabled={!isEditable || isSaving || isCalculating}
              />
            </div>
          );
        }

        const wView = computeWidthPx(value);
        return (
          <div
            style={{
              width: `${wView}px`,
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                display: "inline-block",
                width: `${wView}px`,
                cursor: !isEditable || isSaving ? "default" : "pointer",
                padding: "6px 10px",
                border: "1.5px dashed #B8C2D1",
                borderRadius: "6px",
                background: !isEditable || isSaving ? "#F4F6FA" : "#F9FBFF",
                color: "#1F2937",
              }}
              onClick={() =>
                !isEditable || isSaving
                  ? undefined
                  : onEdit(sectionId, fieldName)
              }
            >
              {isCurrency ? "₸ " : ""}
              {isPercent ? "% " : ""}
              {typeof value === "number"
                ? Number(value.toFixed(2)).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })
                : value}
            </span>
          </div>
        );
      };

      // Проверяем, является ли раздел единым (без блоков)
      const isInsolation = isInsolationSection(sectionId);
      const isGP = isGPSection(sectionId);

      return (
        <div className="expanded-content-mock align-right">
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              marginBottom: "8px",
              fontStyle: "italic",
            }}
          >
            💡 Кликните по числу, чтобы отредактировать. Изменения сохраняются
            автоматически при потере фокуса.
          </div>
          {isInsolation ? (
            // Специальный интерфейс для раздела "Инсоляция" - средняя ставка, трудозатраты и прямые затраты
            <>
              <div className="expanded-title-row-2col">
                <div
                  className="expanded-title-block"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div className="expanded-title">
                    Средняя ставка
                    <br />
                    на отдел
                  </div>
                  <EditableField
                    fieldName="average_salary"
                    value={details.average_salary}
                    isCurrency={true}
                    onSave={handleSaveField}
                    onCancel={handleCancelEdit}
                    isEditing={
                      editingFields[`${sectionId}_average_salary`] !== undefined
                    }
                    onEdit={(sectionId, fieldName) =>
                      handleEditField(
                        sectionId,
                        fieldName,
                        details.average_salary,
                      )
                    }
                    isSaving={savingFields[`${sectionId}_average_salary`]}
                    isCalculating={
                      calculatingFields[`${sectionId}_average_salary`]
                    }
                  />
                </div>
              </div>
              <div
                className="expanded-table-mock"
                style={{ borderBottom: "1px solid #e9ecef" }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th></th>
                      <th style={{ textAlign: "center" }}>Всего</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Трудозатраты (дн)</td>
                      <td
                        style={{
                          padding: "16px 12px",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <EditableField
                            fieldName="total_day_efforts"
                            value={details.total_day_efforts || 0}
                            onSave={handleSaveField}
                            onCancel={handleCancelEdit}
                            isEditing={
                              editingFields[
                                `${sectionId}_total_day_efforts`
                              ] !== undefined
                            }
                            onEdit={(sectionId, fieldName) =>
                              handleEditField(
                                sectionId,
                                fieldName,
                                details.total_day_efforts || 0,
                              )
                            }
                            isSaving={
                              savingFields[`${sectionId}_total_day_efforts`]
                            }
                            isCalculating={
                              calculatingFields[
                                `${sectionId}_total_day_efforts`
                              ]
                            }
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>Прямые затраты (₸)</td>
                      <td style={{ textAlign: "center" }}>
                        <span className="tenge-inline">₸</span>
                        {(
                          constructionData[idx]?.money_expenditures?.direct || 0
                        ).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : isGP ? (
            // Специальный интерфейс для раздела "ГП" - средняя ставка + таблица с данными для чтения
            <>
              <div className="expanded-title-row-2col">
                <div
                  className="expanded-title-block"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div className="expanded-title">
                    Средняя ставка
                    <br />
                    на отдел
                  </div>
                  <EditableField
                    fieldName="average_salary"
                    value={details.average_salary}
                    isCurrency={true}
                    onSave={handleSaveField}
                    onCancel={handleCancelEdit}
                    isEditing={
                      editingFields[`${sectionId}_average_salary`] !== undefined
                    }
                    onEdit={(sectionId, fieldName) =>
                      handleEditField(
                        sectionId,
                        fieldName,
                        details.average_salary,
                      )
                    }
                    isSaving={savingFields[`${sectionId}_average_salary`]}
                    isCalculating={
                      calculatingFields[`${sectionId}_average_salary`]
                    }
                  />
                </div>
              </div>
              <div
                className="expanded-table-mock"
                style={{ borderBottom: "1px solid #e9ecef" }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th></th>
                      <th style={{ textAlign: "center" }}>Всего</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Трудозатраты (дн)</td>
                      <td style={{ textAlign: "center" }}>
                        {parseFloat(
                          constructionData[idx]?.day_efforts?.direct ||
                            constructionData[idx]?.day_efforts?.total ||
                            0,
                        ).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Бонусы в днях (дн)</td>
                      <td style={{ textAlign: "center" }}>
                        {parseFloat(
                          constructionData[idx]?.day_efforts?.bonus || 0,
                        ).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Прямые затраты (₸)</td>
                      <td style={{ textAlign: "center" }}>
                        <span className="tenge-inline">₸</span>
                        {(
                          constructionData[idx]?.money_expenditures?.direct || 0
                        ).toLocaleString()}
                      </td>
                    </tr>
                    <tr>
                      <td>Бонусы в деньгах (₸)</td>
                      <td style={{ textAlign: "center" }}>
                        <span className="tenge-inline">₸</span>
                        {(
                          constructionData[idx]?.money_expenditures?.bonus || 0
                        ).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            // Обычный интерфейс для остальных разделов
            <>
              <div className="expanded-title-row-2col">
                <div
                  className="expanded-title-block"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div className="expanded-title">
                    Средняя ставка
                    <br />
                    на отдел
                  </div>
                  <EditableField
                    fieldName="average_salary"
                    value={details.average_salary}
                    isCurrency={true}
                    onSave={handleSaveField}
                    onCancel={handleCancelEdit}
                    isEditing={
                      editingFields[`${sectionId}_average_salary`] !== undefined
                    }
                    onEdit={(sectionId, fieldName) =>
                      handleEditField(
                        sectionId,
                        fieldName,
                        details.average_salary,
                      )
                    }
                    isSaving={savingFields[`${sectionId}_average_salary`]}
                    isCalculating={
                      calculatingFields[`${sectionId}_average_salary`]
                    }
                  />
                </div>
              </div>
              <div
                className="expanded-table-mock"
                style={{ borderBottom: "1px solid #e9ecef" }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th></th>
                      {details.block_expenditures?.map((block, i) => (
                        <th key={i} style={{ textAlign: "center" }}>
                          {block.block.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Трудозатраты (дн)</td>
                      {details.block_expenditures?.map((block, i) => (
                        <td
                          key={i}
                          style={{
                            padding: "16px 12px",
                            verticalAlign: "middle",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <EditableField
                              fieldName={`block_efforts_${i}`}
                              value={block.day_efforts}
                              onSave={handleSaveField}
                              onCancel={handleCancelEdit}
                              isEditing={
                                editingFields[
                                  `${sectionId}_block_efforts_${i}`
                                ] !== undefined
                              }
                              onEdit={(sectionId, fieldName) =>
                                handleEditField(
                                  sectionId,
                                  fieldName,
                                  block.day_efforts,
                                )
                              }
                              isSaving={
                                savingFields[`${sectionId}_block_efforts_${i}`]
                              }
                              isCalculating={
                                calculatingFields[
                                  `${sectionId}_block_efforts_${i}`
                                ]
                              }
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Бонусы в днях (дн)</td>
                      {details.block_expenditures?.map((block, i) => (
                        <td key={i} style={{ textAlign: "center" }}>
                          {parseFloat(block.bonus_day_efforts || 0).toFixed(2)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Прямые затраты (₸)</td>
                      {details.block_expenditures?.map((block, i) => (
                        <td key={i} style={{ textAlign: "center" }}>
                          <span className="tenge-inline">₸</span>
                          {block.money_expenditures?.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Бонусы в деньгах (₸)</td>
                      {details.block_expenditures?.map((block, i) => (
                        <td key={i} style={{ textAlign: "center" }}>
                          <span className="tenge-inline">₸</span>
                          {block.bonus_money_expenditures?.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      );
    };

    // Синхронизация суммы "% доля" наверх для блокировки кнопки "Сохранить"
    // Делаем вызов только при фактическом изменении значения, избегая циклов
    const updateHeaderRef = useRef(estimateContext?.updateHeaderData);
    useEffect(() => {
      updateHeaderRef.current = estimateContext?.updateHeaderData;
    }, [estimateContext?.updateHeaderData]);

    const headerPercentTotal = useMemo(() => {
      return totalConstructionDesign?.expenditures_percent?.threshold || 0;
    }, [totalConstructionDesign]);

    const prevHeaderPercentRef = useRef(headerPercentTotal);
    useEffect(() => {
      if (prevHeaderPercentRef.current === headerPercentTotal) return;
      prevHeaderPercentRef.current = headerPercentTotal;
      if (updateHeaderRef.current) {
        updateHeaderRef.current({
          total_construction_design: {
            expenditures_percent: { threshold: headerPercentTotal },
          },
        });
      }
    }, [headerPercentTotal]);

    // Сумма долей по таблице (до любых return), для проверки блокировки «Сохранить»
    const percentTotalFromTable = useMemo(
      () =>
        (constructionData || []).reduce(
          (s, item) =>
            s +
            (Number(
              localPercentChanges[item.section?.id] ??
                item.expenditures_percent?.threshold,
            ) || 0),
          0,
        ),
      [constructionData, localPercentChanges],
    );
    const prevPercentTotalRef = useRef(percentTotalFromTable);
    useEffect(() => {
      if (prevPercentTotalRef.current === percentTotalFromTable) return;
      prevPercentTotalRef.current = percentTotalFromTable;
      if (
        typeof percentTotalFromTable === "number" &&
        updateHeaderRef.current
      ) {
        updateHeaderRef.current({ percentTotalFromTable });
      }
    }, [percentTotalFromTable]);

    if (loading) {
      return <div>Загрузка данных...</div>;
    }

    if (estimateNotFound) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            textAlign: "center",
            color: "#666",
          }}
        >
          <div
            style={{
              fontSize: "48px",
              marginBottom: "16px",
              opacity: "0.6",
            }}
          >
            📋
          </div>
          <h3
            style={{
              margin: "0 0 12px 0",
              fontSize: "18px",
              fontWeight: "500",
              color: "#333",
            }}
          >
            Смета еще не создана
          </h3>
          <p
            style={{
              margin: "0",
              fontSize: "14px",
              lineHeight: "1.5",
              maxWidth: "400px",
            }}
          >
            Для отображения данных о строительном проектировании необходимо
            сначала создать смету для данного проекта.
          </p>
        </div>
      );
    }

    if (error) {
      return <div>Ошибка загрузки данных: {error}</div>;
    }

    const tableData = transformApiData(constructionData);
    // Итоги: с бэкенда, но «Доля разделов» и «Доля сметной стоимости» считаем по видимым строкам,
    // чтобы не показывать завышенную сумму из-за дубликатов или рассинхрона с таблицей
    const backendTotals = totalConstructionDesign
      ? getTotalsFromBackend(totalConstructionDesign)
      : null;
    const percentLaborTotalFromTable = tableData.reduce(
      (sum, row) => sum + (parseFloat(row.percentLabor) || 0),
      0,
    );
    const totals =
      backendTotals != null
        ? {
            ...backendTotals,
            percentTotal: percentTotalFromTable,
            percentLaborTotal: percentLaborTotalFromTable,
          }
        : null;

    /** Перестановка разделов по drag-and-drop */
    const handleSectionDragStart = (e, index) => {
      setDraggedRowIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
      e.target.closest("tr")?.classList.add("work-tab-dragging");
    };
    const handleSectionDragEnd = (e) => {
      setDraggedRowIndex(null);
      e.target.closest("tr")?.classList.remove("work-tab-dragging");
    };
    const handleSectionDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    };
    const handleSectionDrop = (e, dropIndex) => {
      e.preventDefault();
      const fromIndex =
        draggedRowIndex ?? parseInt(e.dataTransfer.getData("text/plain"), 10);
      if (Number.isNaN(fromIndex) || fromIndex === dropIndex) return;
      setConstructionData((prev) => {
        const next = [...prev];
        const [removed] = next.splice(fromIndex, 1);
        next.splice(dropIndex, 0, removed);
        // Обновляем порядок в ref по новому индексу строк
        const order = sectionOrderRef.current || {};
        const nextOrder = {};
        next.forEach((item, idx) => {
          const id = item?.section?.id;
          if (id != null) nextOrder[id] = idx;
        });
        sectionOrderRef.current = { ...order, ...nextOrder };
        // Синхронизируем порядок в контекст, чтобы при сохранении ушёл правильный order_index
        setCtxEstimateData((ctx) =>
          ctx ? { ...ctx, construction_design: next } : ctx,
        );
        return next;
      });
      setDraggedRowIndex(null);
    };

    return (
      <>
        {/* Чекбокс «Учитывать бонусы при расчёте» и блок «% доля бонусов» */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "24px",
              flexWrap: "wrap",
            }}
          >
            <label
              style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}
            >
              Учитывать бонусы при расчёте:
            </label>
            <label className="estimate-switch">
              <input
                type="checkbox"
                checked={considerBonuses}
                onChange={(e) => handleIncludeBonusesChange(e.target.checked)}
                disabled={
                  !isEditable || isSaving || isCalculatingIncludeBonuses
                }
              />
              <span className="estimate-slider"></span>
            </label>
            <label
              style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}
            >
              % доля бонусов от ПЗ:
            </label>
            {editingGlobalBonus ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={tempGlobalBonus}
                  ref={globalBonusInputRef}
                  onChange={(e) => setTempGlobalBonus(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      saveEditGlobalBonus(e);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      cancelEditGlobalBonus();
                    }
                  }}
                  style={{
                    width: "30px",
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    textAlign: "center",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ccc";

                    // Проверяем, изменилось ли значение от исходного
                    const originalStr = String(globalBonusPercent);
                    const currentStr = String(tempGlobalBonus);

                    // Если значение не изменилось — просто отменяем редактирование без сохранения
                    if (originalStr === currentStr) {
                      cancelEditGlobalBonus();
                      return;
                    }

                    // Если значение пустое или некорректное — восстанавливаем исходное
                    const numValue = parseFloat(tempGlobalBonus);
                    if (
                      tempGlobalBonus === "" ||
                      Number.isNaN(numValue) ||
                      numValue < 0
                    ) {
                      cancelEditGlobalBonus();
                      return;
                    }

                    // Автоматически сохраняем при потере фокуса только если значение валидно и изменилось
                    saveEditGlobalBonus(e);
                  }}
                  disabled={!isEditable || isSaving}
                />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontFamily: "Manrope, sans-serif",
                    minWidth: "30px",
                    padding: "8px 12px",
                    border: "1.5px dashed #B8C2D1",
                    borderRadius: "6px",
                    background: !isEditable || isSaving ? "#F4F6FA" : "#F9FBFF",
                    textAlign: "center",
                    cursor: !isEditable || isSaving ? "default" : "pointer",
                  }}
                  onClick={
                    !isEditable || isSaving ? undefined : startEditGlobalBonus
                  }
                >
                  {globalBonusPercent}
                </span>
              </div>
            )}
            <span style={{ fontSize: "12px", color: "#666" }}>
              ⚠️ Применяется ко всем разделам
            </span>
            <label
              style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}
            >
              % бонусов АУП и АХО:
            </label>
            {editingAupAhoBonus ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={tempAupAhoBonus}
                  ref={aupAhoBonusInputRef}
                  onChange={(e) => setTempAupAhoBonus(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      saveEditAupAhoBonus(e);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      cancelEditAupAhoBonus();
                    }
                  }}
                  style={{
                    width: "30px",
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    textAlign: "center",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ccc";
                    const originalStr = String(aupAhoBonusPercent);
                    const currentStr = String(tempAupAhoBonus);
                    if (originalStr === currentStr) {
                      cancelEditAupAhoBonus();
                      return;
                    }
                    const numValue = parseFloat(tempAupAhoBonus);
                    if (
                      tempAupAhoBonus === "" ||
                      Number.isNaN(numValue) ||
                      numValue < 0
                    ) {
                      cancelEditAupAhoBonus();
                      return;
                    }
                    saveEditAupAhoBonus(e);
                  }}
                  disabled={!isEditable || isSaving}
                />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontFamily: "Manrope, sans-serif",
                    minWidth: "30px",
                    padding: "8px 12px",
                    border: "1.5px dashed #B8C2D1",
                    borderRadius: "6px",
                    background: !isEditable || isSaving ? "#F4F6FA" : "#F9FBFF",
                    textAlign: "center",
                    cursor: !isEditable || isSaving ? "default" : "pointer",
                  }}
                  onClick={
                    !isEditable || isSaving ? undefined : startEditAupAhoBonus
                  }
                >
                  {aupAhoBonusPercent}
                </span>
              </div>
            )}
          </div>
          {sectionsEditable && (
            <button
              onClick={handleAddSection}
              disabled={!isEditable || isSaving}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                backgroundColor: "#fff",
                color: "#2A5E38",
                border: "none",
                borderRadius: "4px",
                cursor: !isEditable || isSaving ? "not-allowed" : "pointer",
                opacity: !isEditable || isSaving ? 0.6 : 1,
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
              Добавить раздел
            </button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            padding: "12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          <div style={{ fontSize: "14px", color: "#666" }}>
            💡 Изменения пересчитываются сразу при сохранении поля. Кнопка
            "Сохранить" внизу записывает все изменения в базу данных.
            {Object.keys(localPercentChanges).length > 0 && (
              <span style={{ color: "#007bff", fontWeight: "bold" }}>
                {" "}
                * - несохраненные изменения
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {isSaving && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#28a745",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    width: "16px",
                    height: "16px",
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #28a745",
                    borderRadius: "50%",
                    marginRight: "8px",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                Сохранение данных...
              </div>
            )}
            {isAnyCalculating && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#007bff",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    width: "16px",
                    height: "16px",
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #007bff",
                    borderRadius: "50%",
                    marginRight: "8px",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                Выполняется пересчет...
              </div>
            )}
          </div>
        </div>
        {incompleteSectionIds.length > 0 && (
          <div
            style={{
              marginBottom: "12px",
              padding: "10px 12px",
              background: "#fff8f1",
              border: "1px solid #ffe0b2",
              borderRadius: "6px",
              color: "#b26a00",
              fontSize: "13px",
            }}
          >
            ⚠️ Добавлены разделы без обязательных данных. Для обычных разделов
            заполните среднюю ставку и добавьте хотя бы один блок, для раздела
            "Инсоляция" заполните среднюю ставку и трудозатраты в днях, для
            раздела "ГП" заполните среднюю ставку на отдел — иначе сохранение
            будет недоступно.
          </div>
        )}
        <table className="estimate-table">
          <colgroup>
            <col style={{ width: 36, minWidth: 36 }} />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col style={{ width: 48, minWidth: 48 }} />
          </colgroup>
          <thead>
            <tr>
              <th className="work-tab-drag-th"></th>
              <th>Раздел</th>
              <th>Доля разделов</th>
              <th>Пороговая стоимость раздела</th>
              <th>Плановая стоимость раздела</th>
              <th>Доля сметной стоимости</th>
              <th>Стоимость раздела (ПЗ) за м²</th>
              <th>Отклонения</th>
              <th>Прямые затраты/Бонусный фонд (дн)</th>
              <th>Прямые затраты/Бонусный фонд (₸)</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => [
              <tr
                key={idx}
                className={`estimate-table-row estimate-table-row-interactive ${
                  calculatingSections.has(constructionData[idx]?.section?.id)
                    ? "calculating-row"
                    : ""
                }`}
                style={{
                  opacity: calculatingSections.has(
                    constructionData[idx]?.section?.id,
                  )
                    ? 0.6
                    : 1,
                  transition: "opacity 0.3s ease",
                }}
                draggable={isEditable && !isSaving}
                onDragStart={(e) => handleSectionDragStart(e, idx)}
                onDragEnd={handleSectionDragEnd}
                onDragOver={handleSectionDragOver}
                onDrop={(e) => handleSectionDrop(e, idx)}
              >
                <td className="work-tab-drag-td">
                  <span
                    className="work-tab-drag-handle"
                    title="Перетащите для изменения порядка разделов"
                  >
                    ⋮⋮
                  </span>
                </td>
                <td className="section-cell">
                  <span
                    className="section-arrow"
                    onClick={() => handleExpandRow(idx)}
                    style={{
                      cursor: isSaving ? "not-allowed" : "pointer",
                      transform: expandedSectionIds.has(row.sectionId)
                        ? "rotate(90deg)"
                        : "none",
                      opacity: isSaving ? 0.6 : 1,
                    }}
                  >
                    ▶
                  </span>
                  <span
                    className="estimate_section-title"
                    title={row.sectionDescription || undefined}
                  >
                    {row.section}
                    {row.sectionDescription && (
                      <span
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: "normal",
                          marginTop: "2px",
                        }}
                      >
                        {row.sectionDescription}
                      </span>
                    )}
                  </span>
                </td>
                <td style={{ textAlign: "center" }}>
                  <EditablePercentField
                    sectionId={row.sectionId}
                    value={row.percent}
                    onSave={handleSavePercentField}
                    onCancel={handleCancelEdit}
                    isEditing={
                      editingFields[`${row.sectionId}_threshold_percent`] !==
                      undefined
                    }
                    onEdit={(sectionId, fieldName) =>
                      handleEditField(sectionId, fieldName, row.percent)
                    }
                    isSaving={
                      savingFields[`${row.sectionId}_threshold_percent`]
                    }
                    isCalculating={
                      calculatingFields[`${row.sectionId}_threshold_percent`]
                    }
                  />
                </td>
                <TableCell value={row.forecast.toLocaleString()} isSum isBold />
                <TableCell value={row.plan.toLocaleString()} isSum isBold />
                <TableCell value={row.percentLabor} isPercent />
                <TableCell value={row.pricePerM2} />
                <TableCell
                  value={row.deviation.toLocaleString()}
                  isSum
                  isRed={row.deviation < 0}
                  isGreen={row.deviation > 0}
                  isBold
                />
                <TableCell value={row.bonusDirect} isMulti />
                <TableCell value={row.bonusFund} isSum isMulti />
                <td className="action-cell">
                  {sectionsEditable && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteRow(idx)}
                      title="Удалить раздел"
                      disabled={!isEditable || isSaving}
                      style={{
                        background: "none",
                        border: "none",
                        cursor:
                          !isEditable || isSaving ? "not-allowed" : "pointer",
                        padding: "4px",
                        borderRadius: "4px",
                        color: "#dc3545",
                        opacity: !isEditable || isSaving ? 0.6 : 1,
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (isEditable && !isSaving) {
                          e.target.style.backgroundColor = "#f8d7da";
                          e.target.style.color = "#721c24";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isEditable && !isSaving) {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = "#dc3545";
                        }
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h10.667z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>,
              expandedSectionIds.has(row.sectionId) && (
                <tr
                  key={"expanded-" + row.sectionId}
                  className="estimate-table-expanded-row"
                >
                  <td colSpan={11}>{renderExpandedContent(idx)}</td>
                </tr>
              ),
            ])}
            {/* Итоговая строка */}
            <tr className="estimate-table-total-row">
              <td className="work-tab-drag-td"></td>
              <td className="section-cell">
                <span className="estimate_section-title">Итого</span>
              </td>
              {totals ? (
                <>
                  <td style={{ textAlign: "center" }}>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: totals.percentTotal > 101 ? "#dc3545" : "#333",
                        backgroundColor:
                          totals.percentTotal > 101 ? "#f8d7da" : "transparent",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    >
                      {totals.percentTotal.toFixed(2)}%
                    </span>
                  </td>
                  <TableCell
                    value={totals.forecast.toLocaleString()}
                    isSum
                    isBold
                  />
                  <TableCell
                    value={totals.plan.toLocaleString()}
                    isSum
                    isBold
                  />
                  <td style={{ textAlign: "center" }}>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color:
                          totals.percentLaborTotal > 101 ? "#dc3545" : "#333",
                        backgroundColor:
                          totals.percentLaborTotal > 101
                            ? "#f8d7da"
                            : "transparent",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    >
                      {totals.percentLaborTotal.toFixed(2)}%
                    </span>
                  </td>
                  <td></td>
                  <TableCell
                    value={totals.deviation.toLocaleString()}
                    isSum
                    isRed={totals.deviation < 0}
                    isGreen={totals.deviation > 0}
                    isBold
                  />
                  <td></td>
                  <TableCell value={totals.bonusFund} isSum isMulti />
                  <td></td>
                </>
              ) : (
                <td
                  colSpan={9}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#666",
                  }}
                >
                  Нет данных
                </td>
              )}
            </tr>
          </tbody>
        </table>
        <DeleteModal
          open={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          sectionName={
            deleteRowIndex !== null ? tableData[deleteRowIndex]?.section : ""
          }
        />
        <SuccessModal
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
          }}
          message="Данные успешно сохранены"
        />
        <ErrorModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          message={errorMessage}
        />

        {/* Попап для добавления секции */}
        {showAddSectionModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={handleCancelAddSection}
          >
            <div
              className="add-section-modal"
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Добавление раздела в Рабочее проектирование
              </h3>

              {loadingSections ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "40px",
                    color: "#666",
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      width: "20px",
                      height: "20px",
                      border: "2px solid #f3f3f3",
                      borderTop: "2px solid #2A5E38",
                      borderRadius: "50%",
                      marginRight: "12px",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  Загрузка секций...
                </div>
              ) : (
                <>
                  {/* Выбор типа раздела: с блоками или без блоков */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Тип раздела
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        <input
                          type="radio"
                          name="sectionType"
                          value="with_blocks"
                          checked={sectionType === "with_blocks"}
                          onChange={(e) => setSectionType(e.target.value)}
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span>с блоками</span>
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        <input
                          type="radio"
                          name="sectionType"
                          value="without_blocks"
                          checked={sectionType === "without_blocks"}
                          onChange={(e) => setSectionType(e.target.value)}
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span>без блоков</span>
                      </label>
                    </div>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Выберите раздел из списка*
                    </label>
                    <input
                      type="text"
                      placeholder="Поиск"
                      value={addSectionSearch}
                      onChange={(e) => setAddSectionSearch(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        marginBottom: "10px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                    <div
                      style={{
                        maxHeight: "240px",
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        padding: "8px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {sectionsList.length === 0 ? (
                        <div
                          style={{
                            padding: "16px",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          Нет доступных разделов
                        </div>
                      ) : (
                        (() => {
                          const searchLower = (addSectionSearch || "")
                            .toLowerCase()
                            .trim();
                          const filtered = searchLower
                            ? sectionsList.filter(
                                (s) =>
                                  (s.label || "")
                                    .toLowerCase()
                                    .includes(searchLower) ||
                                  (s.description || "")
                                    .toLowerCase()
                                    .includes(searchLower),
                              )
                            : sectionsList;
                          return filtered.length === 0 ? (
                            <div
                              style={{
                                padding: "16px",
                                color: "#666",
                                fontSize: "14px",
                              }}
                            >
                              Ничего не найдено
                            </div>
                          ) : (
                            filtered.map((section) => {
                              const id = section.value;
                              const checked = selectedSections.includes(id);
                              const alreadyInTable = constructionData.some(
                                (s) => s.section?.id === id,
                              );
                              return (
                                <label
                                  key={id}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    padding: "8px 6px",
                                    cursor: alreadyInTable
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: alreadyInTable ? 0.6 : 1,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      if (alreadyInTable) return;
                                      setSelectedSections((prev) =>
                                        e.target.checked
                                          ? [...prev, id]
                                          : prev.filter((x) => x !== id),
                                      );
                                    }}
                                    disabled={alreadyInTable}
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      cursor: alreadyInTable
                                        ? "not-allowed"
                                        : "pointer",
                                    }}
                                  />
                                  <span className="section-label-text">
                                    <span className="section-label-name">
                                      {section.label}
                                      {alreadyInTable && " (уже добавлен)"}
                                    </span>
                                    {section.description && (
                                      <span className="section-label-description">
                                        {section.description}
                                      </span>
                                    )}
                                  </span>
                                </label>
                              );
                            })
                          );
                        })()
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                      marginTop: "24px",
                    }}
                  >
                    <button
                      onClick={handleCancelAddSection}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        color: "#666",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={handleConfirmAddSection}
                      disabled={selectedSections.length === 0}
                      style={{
                        padding: "8px 16px",
                        border: "none",
                        borderRadius: "4px",
                        backgroundColor:
                          selectedSections.length > 0 ? "#2a5e38" : "#ccc",
                        color: "white",
                        cursor:
                          selectedSections.length > 0
                            ? "pointer"
                            : "not-allowed",
                        fontSize: "14px",
                        opacity: selectedSections.length > 0 ? 1 : 0.6,
                      }}
                    >
                      Добавить
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  },
);

export default WorkTab;
